import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ChevronRight, ChevronLeft, ChevronDown, Palette, Settings, BarChart3, Upload, LayoutDashboard, Shield, Crown, Target, Plus, ArrowRight, Loader2, Globe } from 'lucide-react';
import { ComponentSidebar } from '../components/Builder/ComponentSidebar';
import { Canvas } from '../components/Builder/Canvas';
import { SettingsPanel } from '../components/Builder/SettingsPanel';
import { ToggleSwitch } from '../components/Common/ToggleSwitch';
import { PageMetaCapiSelector } from '../components/Common/PageMetaCapiSelector';
import { AffiliatePostbackPanel } from '../components/Common/AffiliatePostbackPanel';
import { DevDocShareCard } from '../components/Common/DevDocShareCard';
import { GtmAutoSyncPanel } from '../components/Common/GtmAutoSyncPanel';
import { TrackingSection } from '../components/Builder/TrackingSection';
import {
  FacebookBrandIcon,
  MetaBrandIcon,
  TikTokBrandIcon,
  GoogleAnalyticsBrandIcon,
  GoogleTagManagerBrandIcon,
} from '../components/Common/BrandIcons';
import { InfoTooltip } from '../components/Common/InfoTooltip';
import { useBuilderStore } from '../store/builderStore';
import { useAuthStore } from '../store/authStore';
import { useAccountStore, useEffectivePlan } from '../store/accountStore';
import EditSlugModal from '../components/User/EditSlugModal';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/Common/LanguageSwitcher';
import { hasFeature, isPaidTier } from '../constants/planLimits';

// Environment variables
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const APP_URL = import.meta.env.VITE_APP_URL || 'https://hitzlink.com';
const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || 'hitzlink.com';

const steps = [
  { number: 1, key: 'design', icon: <Palette size={16} /> },
  { number: 2, key: 'settings', icon: <Settings size={16} /> },
  { number: 3, key: 'tracking', icon: <BarChart3 size={16} /> },
  { number: 4, key: 'protection', icon: <Shield size={16} /> },
  { number: 5, key: 'publish', icon: <Upload size={16} /> },
] as const;

// Country list for the geo-fence picker. ISO 3166-1 alpha-2 codes match the
// `cf-ipcountry` header from Cloudflare so the comparison on the server is
// always 1:1. Grouped by region for easier scanning in the dropdown.
type Country = { code: string; name: string; flag: string; region: string };
const COUNTRIES: Country[] = [
  // Southeast Asia
  { code: 'TH', name: 'Thailand',     flag: '🇹🇭', region: 'Southeast Asia' },
  { code: 'VN', name: 'Vietnam',      flag: '🇻🇳', region: 'Southeast Asia' },
  { code: 'ID', name: 'Indonesia',    flag: '🇮🇩', region: 'Southeast Asia' },
  { code: 'PH', name: 'Philippines',  flag: '🇵🇭', region: 'Southeast Asia' },
  { code: 'MY', name: 'Malaysia',     flag: '🇲🇾', region: 'Southeast Asia' },
  { code: 'SG', name: 'Singapore',    flag: '🇸🇬', region: 'Southeast Asia' },
  { code: 'KH', name: 'Cambodia',     flag: '🇰🇭', region: 'Southeast Asia' },
  { code: 'LA', name: 'Laos',         flag: '🇱🇦', region: 'Southeast Asia' },
  { code: 'MM', name: 'Myanmar',      flag: '🇲🇲', region: 'Southeast Asia' },
  { code: 'BN', name: 'Brunei',       flag: '🇧🇳', region: 'Southeast Asia' },
  // East Asia
  { code: 'JP', name: 'Japan',        flag: '🇯🇵', region: 'East Asia' },
  { code: 'KR', name: 'South Korea',  flag: '🇰🇷', region: 'East Asia' },
  { code: 'TW', name: 'Taiwan',       flag: '🇹🇼', region: 'East Asia' },
  { code: 'HK', name: 'Hong Kong',    flag: '🇭🇰', region: 'East Asia' },
  { code: 'CN', name: 'China',        flag: '🇨🇳', region: 'East Asia' },
  // South Asia
  { code: 'IN', name: 'India',        flag: '🇮🇳', region: 'South Asia' },
  { code: 'BD', name: 'Bangladesh',   flag: '🇧🇩', region: 'South Asia' },
  { code: 'PK', name: 'Pakistan',     flag: '🇵🇰', region: 'South Asia' },
  { code: 'LK', name: 'Sri Lanka',    flag: '🇱🇰', region: 'South Asia' },
  { code: 'NP', name: 'Nepal',        flag: '🇳🇵', region: 'South Asia' },
  // Oceania
  { code: 'AU', name: 'Australia',    flag: '🇦🇺', region: 'Oceania' },
  { code: 'NZ', name: 'New Zealand',  flag: '🇳🇿', region: 'Oceania' },
  // North America
  { code: 'US', name: 'United States', flag: '🇺🇸', region: 'North America' },
  { code: 'CA', name: 'Canada',       flag: '🇨🇦', region: 'North America' },
  { code: 'MX', name: 'Mexico',       flag: '🇲🇽', region: 'North America' },
  // Europe
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', region: 'Europe' },
  { code: 'IE', name: 'Ireland',      flag: '🇮🇪', region: 'Europe' },
  { code: 'DE', name: 'Germany',      flag: '🇩🇪', region: 'Europe' },
  { code: 'FR', name: 'France',       flag: '🇫🇷', region: 'Europe' },
  { code: 'ES', name: 'Spain',        flag: '🇪🇸', region: 'Europe' },
  { code: 'IT', name: 'Italy',        flag: '🇮🇹', region: 'Europe' },
  { code: 'NL', name: 'Netherlands',  flag: '🇳🇱', region: 'Europe' },
  { code: 'PL', name: 'Poland',       flag: '🇵🇱', region: 'Europe' },
  { code: 'PT', name: 'Portugal',     flag: '🇵🇹', region: 'Europe' },
  { code: 'SE', name: 'Sweden',       flag: '🇸🇪', region: 'Europe' },
  { code: 'NO', name: 'Norway',       flag: '🇳🇴', region: 'Europe' },
  { code: 'DK', name: 'Denmark',      flag: '🇩🇰', region: 'Europe' },
  { code: 'FI', name: 'Finland',      flag: '🇫🇮', region: 'Europe' },
  { code: 'CH', name: 'Switzerland',  flag: '🇨🇭', region: 'Europe' },
  { code: 'AT', name: 'Austria',      flag: '🇦🇹', region: 'Europe' },
  { code: 'BE', name: 'Belgium',      flag: '🇧🇪', region: 'Europe' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', region: 'Europe' },
  { code: 'GR', name: 'Greece',       flag: '🇬🇷', region: 'Europe' },
  { code: 'RO', name: 'Romania',      flag: '🇷🇴', region: 'Europe' },
  { code: 'TR', name: 'Turkey',       flag: '🇹🇷', region: 'Europe' },
  { code: 'RU', name: 'Russia',       flag: '🇷🇺', region: 'Europe' },
  // South America
  { code: 'BR', name: 'Brazil',       flag: '🇧🇷', region: 'South America' },
  { code: 'AR', name: 'Argentina',    flag: '🇦🇷', region: 'South America' },
  { code: 'CL', name: 'Chile',        flag: '🇨🇱', region: 'South America' },
  { code: 'CO', name: 'Colombia',     flag: '🇨🇴', region: 'South America' },
  { code: 'PE', name: 'Peru',         flag: '🇵🇪', region: 'South America' },
  // Middle East / Africa
  { code: 'AE', name: 'UAE',          flag: '🇦🇪', region: 'Middle East / Africa' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', region: 'Middle East / Africa' },
  { code: 'IL', name: 'Israel',       flag: '🇮🇱', region: 'Middle East / Africa' },
  { code: 'EG', name: 'Egypt',        flag: '🇪🇬', region: 'Middle East / Africa' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', region: 'Middle East / Africa' },
  { code: 'NG', name: 'Nigeria',      flag: '🇳🇬', region: 'Middle East / Africa' },
];

const COUNTRY_BY_CODE: Record<string, Country> = COUNTRIES.reduce((acc, c) => {
  acc[c.code] = c;
  return acc;
}, {} as Record<string, Country>);

const COUNTRY_PRESETS: { id: string; label: string; codes: string[] }[] = [
  { id: 'th',  label: '🇹🇭 Thailand only',    codes: ['TH'] },
  { id: 'sea', label: '🌏 SE Asia',            codes: ['TH', 'VN', 'ID', 'PH', 'MY', 'SG', 'KH', 'LA', 'MM'] },
  { id: 'asia', label: '🗾 Asia (broad)',      codes: ['TH', 'VN', 'ID', 'PH', 'MY', 'JP', 'KR', 'TW', 'HK', 'IN'] },
];

export const Builder: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('builder');
  const { t: tCommon } = useTranslation('common');
  const { 
    currentPage, 
    setCurrentPage,
    updatePage,
    savePage, 
    isSaving, 
    pages,
    loadPages,
    createPage
  } = useBuilderStore();
  const { user, token, checkAuth } = useAuthStore();
  // Builder is outside UserLayout — load workspace accounts here so
  // plan gates use the active workspace owner's plan (Enterprise team
  // members must not fall back to their personal Free subscription).
  const loadAccounts = useAccountStore((s) => s.loadAccounts);
  const accountsLoaded = useAccountStore((s) => s.loaded);
  const effectivePlan = useEffectivePlan();

  React.useEffect(() => {
    if (!accountsLoaded) {
      void loadAccounts();
    }
  }, [accountsLoaded, loadAccounts]);

  const [currentStep, setCurrentStep] = React.useState(1);
  // Mobile-only state for the two side panels. Below the lg breakpoint
  // we hide the ComponentSidebar by default; this state opens it as a
  // slide-in drawer. The SettingsPanel uses `selectedComponent` as its
  // own implicit trigger and doesn't need a separate flag.
  const [componentsSidebarMobileOpen, setComponentsSidebarMobileOpen] = React.useState(false);
  // Timestamp of the last successful save. Bumped on every Save click
  // so child components (DevDocShareCard, anywhere else that derives
  // server-side state from this page) know to re-fetch without a tab
  // focus / page reload. We track the millis instead of a boolean
  // counter so React always sees a NEW value when the page is saved
  // twice in a row.
  const [lastSavedAt, setLastSavedAt] = React.useState<number>(0);
  const [googleAnalyticsId, setGoogleAnalyticsId] = React.useState('');
  const [googleTagManagerId, setGoogleTagManagerId] = React.useState('');
  const [facebookPixelIds, setFacebookPixelIds] = React.useState<string[]>([]);
  const [tiktokPixelId, setTiktokPixelId] = React.useState('');
  // Per-page Meta CAPI selection. The user can either:
  //   - pick one or more tokens from the account-level pool (tokenIds[]), OR
  //   - paste a custom override (accessToken)
  //   - or inherit the account default (no fields set)
  // `tokenId` is the legacy single-select shape — still readable from
  // older pages but new writes always use tokenIds[].
  const [metaCapi, setMetaCapi] = React.useState<{
    tokenIds?: string[];
    tokenId?: string;
    accessToken?: string;
    testEventCode?: string;
    enabled?: boolean;
  }>({});
  // Account-level token pool (id + display label) for the picker. Loaded
  // once on Builder mount; we don't need the masked secret here, just
  // the metadata to render the dropdown.
  const [accountTokens, setAccountTokens] = React.useState<Array<{
    id: string;
    name: string;
    pixelId: string;
  }>>([]);
  const [accountDefaultTokenId, setAccountDefaultTokenId] = React.useState<string | null>(null);
  // Convenience flag — true when the operator has any account-level CAPI
  // token configured (used to render the "inheriting" hint in the panel).
  // Prefixed with _ until the inheriting hint is wired up.
  const _accountHasCapi = accountTokens.length > 0;
  // Account-level Pixel inventory (sourced from Pixel Manager). Lets
  // the Builder render a Picker over previously-configured pixels so
  // operators don't have to retype IDs they've already added.
  const [accountPixels, setAccountPixels] = React.useState<Array<{
    pixelId: string;
    usedOnPages: number;
    primaryOn: number;
    role: 'primary' | 'shadow' | 'mixed';
  }>>([]);
  const [accountPixelsLoading, setAccountPixelsLoading] = React.useState(true);
  // Custom domains linked to ANY of the user's pages. We fetch the full
  // list once (cheap — capped at a handful per plan tier) and then
  // filter in render by currentPage.id. Lets the Page Settings step
  // surface "this page is also live at managolfshop.club" so users
  // don't go hunting for a per-page selector that doesn't exist
  // (linkage is set in the Custom Domain page, not the builder).
  const [accountCustomDomains, setAccountCustomDomains] = React.useState<Array<{
    id: string;
    hostname: string;
    pageId: string;
    status: 'pending' | 'active' | 'failed';
  }>>([]);
  // Per-provider master toggles. UI-only state — initialized from
  // "has value" when a page loads so existing pages render with the
  // correct providers expanded. Toggling OFF clears the corresponding
  // value(s) so the provider truly stops firing.
  const [fbPixelEnabled, setFbPixelEnabled] = React.useState(false);
  const [tiktokEnabled, setTiktokEnabled] = React.useState(false);
  const [gaEnabled, setGaEnabled] = React.useState(false);
  const [gtmEnabled, setGtmEnabled] = React.useState(false);
  const [affiliatePostbackEnabled, setAffiliatePostbackEnabled] = React.useState(false);
  const [affiliatePostbackSaving, setAffiliatePostbackSaving] = React.useState(false);
  const [seoIndexing, setSeoIndexing] = React.useState(true);
  const [showBranding, setShowBranding] = React.useState(false);
  const [cloakingEnabled, setCloakingEnabled] = React.useState(false);
  const [safePageUrl, setSafePageUrl] = React.useState('');
  const [safePageTemplate, setSafePageTemplate] = React.useState('auto');
  const [adNiche, setAdNiche] = React.useState('general');
  // Bot Cloaking Level 3 — optional user-supplied theme for the AI
  // safe page generator. Free text, max 200 chars. Pre-validated server-
  // side against the niche's must_avoid list (a topic that would defeat
  // the cloak gets rejected before reaching the model).
  // Empty string = let the model pick from the niche topic_pool.
  const [aiCustomTopic, setAiCustomTopic] = React.useState('');
  const [aiCustomTopicError, setAiCustomTopicError] = React.useState<string | null>(null);
  /** AI safe-page article language; persisted as `settings.lang` (server: th|en). */
  const [aiSafePageLang, setAiSafePageLang] = React.useState<'en' | 'th'>('en');
  const [safePageCatalog, setSafePageCatalog] = React.useState<{
    templates: { id: string; name: string; category: string; lang: string; niches?: string[] }[];
    categories: { id: string; label: string }[];
    niches: { id: string; label: string; description?: string; templateCount: number }[];
  }>({ templates: [], categories: [], niches: [] });
  const [safePagePick, setSafePagePick] = React.useState<{ id: string; name: string; category: string; lang: string } | null>(null);
  // Bot Cloaking Level 3 — AI-generated safe pages
  type AiSafePageImage = {
    url: string;
    alt?: string;
    source?: 'unsplash' | 'picsum';
    credit?: {
      name: string;
      profileUrl: string;
      sourceName?: string;
      sourceUrl?: string;
    } | null;
  };
  type AiSafePageVariation = {
    slug: string;
    title: string;
    description: string;
    topic_seed: string;
    tags: string[];
    images?: AiSafePageImage[];
  };
  type AiSafePageMeta = {
    id: string;
    niche: string;
    lang: string;
    model: string;
    generated_at: string;
    input_tokens: number;
    output_tokens: number;
    cost_usd: number;
    /** Number of regenerations performed after the initial generation. */
    regenerations_used?: number;
    /** Hard cap (defaults to 1 — i.e. user can regenerate once total). */
    regenerations_max?: number;
    /**
     * Echo of the user-supplied theme the article was generated for.
     * Empty string means "no custom theme — used the niche topic_pool".
     * Used by the Builder to rehydrate the input field on reload.
     */
    custom_topic?: string;
  };
  const [aiSafePages, setAiSafePages] = React.useState<{ exists: boolean; meta?: AiSafePageMeta; variations: AiSafePageVariation[] }>({ exists: false, variations: [] });
  const [aiVariationIndex, setAiVariationIndex] = React.useState<number | undefined>(undefined);
  const [isGeneratingAi, setIsGeneratingAi] = React.useState(false);
  // Geo-fence — only listed countries see the money page; everyone else
  // gets a safe page. Stored as an array of ISO 3166-1 alpha-2 codes
  // (e.g. ["TH"]). Empty array = no geo restriction.
  const [allowedCountries, setAllowedCountries] = React.useState<string[]>([]);
  // City-level geo-fence (optional, narrows after country check passes).
  // Stored as an array of city names (free-text; matched case- and
  // whitespace-insensitively on the server). Empty = no city restriction.
  const [allowedCities, setAllowedCities] = React.useState<string[]>([]);
  const [cityInput, setCityInput] = React.useState('');
  // ASN-level controls.
  //
  // `autoBlockDatacenters` is now a hard-coded `true` whenever the user
  // saves the page — datacenter / VPS / VPN traffic is the #1 source of
  // bot reviewers and the average operator doesn't know what an ASN is,
  // so the UI for this toggle was removed. It still rides under the
  // master "Bot Cloaking" switch, so disabling cloaking turns it off
  // implicitly (the middleware doesn't run).
  //
  // `customBlockedAsns` similarly has no UI any more, but we keep the
  // state and the load/save round-trip so legacy pages that already had
  // a custom list saved don't silently lose those entries.
  const autoBlockDatacenters = true;
  const [customBlockedAsns, setCustomBlockedAsns] = React.useState<number[]>([]);
  // Whether the "Advanced settings" disclosure inside the cloaking
  // panel (custom ASN list, custom safe-page URL) is expanded.
  // Defaults to collapsed because >95% of users never need either —
  // when the page already has a custom ASN saved or a custom safe-URL
  // set we auto-expand at render time so we never hide existing
  // configuration.
  const [advancedCloakingOpen, setAdvancedCloakingOpen] = React.useState(false);
  const [redirectEnabled, setRedirectEnabled] = React.useState(false);
  const [desktopUrl, setDesktopUrl] = React.useState('');
  const [mobileUrl, setMobileUrl] = React.useState('');
  const [tabletUrl, setTabletUrl] = React.useState('');
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [showSlugModal, setShowSlugModal] = React.useState(false);

  useEffect(() => {
    // Refresh user data from backend to ensure subscription is up-to-date
    checkAuth();
    
    // Load pages first
    loadPages();
    
    // Check if user can create new page
    const isFreeUser = !isPaidTier(effectivePlan);
    const canCreatePage = !isFreeUser || pages.length < 3;
    
    // Initialize with a default page if none exists
    if (!currentPage && pages.length === 0) {
      if (canCreatePage) {
        createPage('Untitled Landing Page').catch((err: Error) => {
          console.error('Failed to create initial page:', err);
          toast.error(err.message || 'Failed to create page. Please try again.');
          navigate('/dashboard');
        });
      } else {
        toast.error('Free users can create up to 3 landing pages. Please upgrade to Pro.');
        navigate('/dashboard');
      }
    } else if (!currentPage && pages.length > 0) {
      // If pages exist but no current page, use the first one
      // setCurrentPage already clears selectedComponent
      setCurrentPage(pages[0]);
    }
    // NOTE: stale-currentPage validation runs in the dedicated useEffect
    // below — `pages` is empty on this first effect tick anyway.

    // One-shot probe so the per-page CAPI panel can show the
    // "inheriting account default" hint when the user already pasted
    // a token at the account level. Failure is silent — first-time
    // users will just see no hint, which is correct.
    (async () => {
      try {
        // Auth store persists token under localStorage key 'token'
        // (not 'auth-token' — that key doesn't exist anywhere in
        // authStore). Fall back to in-memory state for the brief
        // window after login before zustand serializes.
        const t =
          useAuthStore.getState().token ||
          localStorage.getItem('token') ||
          '';
        if (!t) return;
        const res = await fetch('https://api.hitzlink.com/api/auth/me/capi-settings', {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        // New shape returns a `tokens` array. Defensive — older builds
        // could return only legacy fields, in which case we synthesize
        // a single virtual entry so the picker still has something to
        // show ("Account default · …").
        const tokens = Array.isArray(data.tokens) && data.tokens.length
          ? data.tokens.map((t: any) => ({
              id: String(t.id),
              name: String(t.name || 'Token'),
              pixelId: String(t.pixelId || ''),
            }))
          : (data.hasAccessToken
            ? [{ id: 'legacy', name: 'Default', pixelId: '' }]
            : []);
        setAccountTokens(tokens);
        setAccountDefaultTokenId(data.defaultTokenId || tokens[0]?.id || null);
      } catch { /* swallow */ }
    })();

    // Load Pixel Manager inventory so the Builder's Pixel section can
    // show a picker over already-configured pixels instead of asking
    // the operator to retype the ID. Failure is silent — the picker
    // will just show the empty state with a "Go to Pixel Manager" CTA.
    (async () => {
      try {
        const t =
          useAuthStore.getState().token ||
          localStorage.getItem('token') ||
          '';
        if (!t) {
          setAccountPixelsLoading(false);
          return;
        }
        const res = await fetch(`${API_URL}/pixel-manager/inventory`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (!res.ok) {
          setAccountPixelsLoading(false);
          return;
        }
        const data = await res.json();
        const pixels = Array.isArray(data?.pixels) ? data.pixels : [];
        setAccountPixels(pixels.map((p: any) => ({
          pixelId: String(p.pixelId),
          usedOnPages: Number(p.usedOnPages) || 0,
          primaryOn: Number(p.primaryOn) || 0,
          role: (p.role === 'primary' || p.role === 'mixed') ? p.role : 'shadow',
        })));
      } catch { /* swallow */ } finally {
        setAccountPixelsLoading(false);
      }
    })();

    // Load the user's custom domains so Page Settings (step 2) can
    // show which domain(s) currently point at the page being edited.
    // No loading flag on purpose — render shows the "no custom domain
    // yet" hint while this in-flight, then upgrades to chips when the
    // fetch resolves. Failure is silent (Free / non-configured users
    // get a 403/503 → empty array → hint), so the builder stays usable
    // regardless of the custom-domain feature state.
    (async () => {
      try {
        const t =
          useAuthStore.getState().token ||
          localStorage.getItem('token') ||
          '';
        if (!t) return;
        const res = await fetch(`${API_URL}/custom-domains`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const rows = Array.isArray(data?.domains) ? data.domains : [];
        setAccountCustomDomains(rows.map((d: any) => ({
          id: String(d.id),
          hostname: String(d.hostname),
          pageId: String(d.pageId || ''),
          status: d.status === 'active' || d.status === 'failed' ? d.status : 'pending',
        })));
      } catch { /* swallow */ }
    })();
  }, []); // Run only once on mount

  /**
   * Stale `currentPage` self-heal.
   *
   * Why this exists:
   *
   * `useBuilderStore` persists `currentPage` to localStorage (zustand
   * `persist` middleware). When the operator returns to /builder days
   * later, that snapshot is rehydrated immediately — including its
   * `id`. If the row was deleted, transferred, or the operator is
   * logged into a different account than when it was persisted, the
   * Builder will silently render that ghost page from cache and every
   * server call keyed on `currentPage.id` (CAPI/affiliate-config,
   * dev-doc share link, save's PUT) will 404.
   *
   * The symptom we kept hitting: "I clicked Save and it said success
   * but the share link still shows 'Save first'." The Save success
   * toast came from a save that went through the *previous* page id;
   * the share card was reading the new (stale) one.
   *
   * Once `loadPages()` returns, we know which pages actually exist
   * for the current user. If the persisted `currentPage.id` isn't in
   * that set, we swap to the first real page so every downstream
   * fetch keys on a row the server can find. Toast tells the operator
   * what happened so they aren't confused if their unsaved tweaks
   * "disappear" (they were never against a real page anyway).
   */
  useEffect(() => {
    if (pages.length === 0) return;
    if (!currentPage) return;
    const stillExists = pages.some((p) => p.id === currentPage.id);
    if (!stillExists) {
      console.warn(
        '[Builder] currentPage.id',
        currentPage.id,
        'not in loaded pages — swapping to',
        pages[0].id
      );
      setCurrentPage(pages[0]);
      toast.dismiss();
      toast(
        i18n.language === 'th'
          ? 'หน้าที่กำลังแก้ไขไม่พบแล้ว — สลับไปหน้าล่าสุดให้แทน'
          : 'The page you were editing is no longer available — switched to your latest page.',
        { icon: 'ℹ️', duration: 5000 }
      );
    }
  }, [pages, currentPage?.id, setCurrentPage]);

  // Load settings from current page
  useEffect(() => {
    if (currentPage) {
      // Analytics - with safe fallbacks for old data
      const ga = currentPage.settings.analytics?.googleAnalyticsId || '';
      const gtm = currentPage.settings.analytics?.googleTagManagerId || '';
      const tiktok = currentPage.settings.analytics?.tiktokPixelId || '';
      const pixelIds = currentPage.settings.analytics?.facebookPixelIds || [];

      setGoogleAnalyticsId(ga);
      setGoogleTagManagerId(gtm);
      setFacebookPixelIds(pixelIds);
      setTiktokPixelId(tiktok);
      setMetaCapi(currentPage.settings.analytics?.metaCapi || {});

      // Derive per-provider toggle state from "has value" so legacy
      // pages render with the correct providers expanded automatically.
      setFbPixelEnabled(pixelIds.some((id: string) => id && id.trim() !== ''));
      setTiktokEnabled(tiktok.trim() !== '');
      setGaEnabled(ga.trim() !== '');
      setGtmEnabled(gtm.trim() !== '');
      setAffiliatePostbackEnabled((currentPage.settings as any).affiliate?.enabled !== false);
      
      // SEO Indexing (default: true if not set)
      setSeoIndexing(currentPage.settings.seo?.indexing !== false);
      
      // Branding Watermark (default: off for paid tiers, forced on for free)
      setShowBranding(
        isPaidTier(effectivePlan)
          ? currentPage.settings.branding?.show === true
          : currentPage.settings.branding?.show !== false
      );
      
      // Cloaking & Redirect — never hydrate ON for tiers that cannot
      // persist it (leftover enabled flag was 403ing pixel saves).
      setCloakingEnabled(
        hasFeature(effectivePlan, 'cloaking') &&
          Boolean(currentPage.settings.cloaking?.enabled)
      );
      {
        const savedUrl = currentPage.settings.cloaking?.safePageUrl || '';
        // Treat legacy default ("google.com") as empty so the user gets the inline template flow.
        const isLegacyDefault =
          savedUrl === 'https://www.google.com' || savedUrl === 'https://google.com';
        setSafePageUrl(isLegacyDefault ? '' : savedUrl);
      }
      setSafePageTemplate(currentPage.settings.cloaking?.safePageTemplate || 'auto');
      setAdNiche(currentPage.settings.cloaking?.adNiche || 'general');
      {
        const L = (currentPage.settings as { lang?: string }).lang;
        setAiSafePageLang(L === 'th' ? 'th' : 'en');
      }
      {
        const saved = currentPage.settings.cloaking?.allowedCountries;
        if (Array.isArray(saved)) {
          setAllowedCountries(saved.filter((c: unknown): c is string => typeof c === 'string' && /^[A-Z]{2}$/.test(c)));
        } else if (typeof saved === 'string' && saved) {
          // Legacy: comma-separated string saved by an earlier build
          setAllowedCountries(saved.split(/[,\s]+/).map(s => s.trim().toUpperCase()).filter(s => /^[A-Z]{2}$/.test(s)));
        } else {
          setAllowedCountries([]);
        }
      }
      {
        const savedCities = currentPage.settings.cloaking?.allowedCities;
        if (Array.isArray(savedCities)) {
          setAllowedCities(
            savedCities.filter((c: unknown): c is string => typeof c === 'string' && c.trim().length > 0),
          );
        } else {
          setAllowedCities([]);
        }
        setCityInput('');
      }
      {
        // ASN settings — `autoBlockDatacenters` is now hard-coded true at
        // the source of truth (declared above). We deliberately don't
        // restore an old `false` value so legacy pages that opted out of
        // auto-block re-inherit the recommended default on next save.
        const savedAsns = currentPage.settings.cloaking?.blockedAsns;
        if (Array.isArray(savedAsns)) {
          setCustomBlockedAsns(
            savedAsns
              .map((a: unknown) => (typeof a === 'number' ? a : parseInt(String(a), 10)))
              .filter((n: number) => Number.isFinite(n) && n > 0),
          );
        } else {
          setCustomBlockedAsns([]);
        }
      }
      {
        const savedIdx = currentPage.settings.cloaking?.aiVariationIndex;
        setAiVariationIndex(
          Number.isInteger(savedIdx) && savedIdx! >= 0 && savedIdx! <= 2 ? savedIdx : undefined,
        );
      }
      // Reset AI cache when switching pages — refetched by the effect below.
      setAiSafePages({ exists: false, variations: [] });
      // Reset the custom-topic input when switching pages. The fetch
      // effect rehydrates it from meta.custom_topic if a previous
      // generation used one.
      setAiCustomTopic('');
      setAiCustomTopicError(null);
      setRedirectEnabled(currentPage.settings.redirect?.enabled || false);
      setDesktopUrl(currentPage.settings.redirect?.desktopUrl || '');
      setMobileUrl(currentPage.settings.redirect?.mobileUrl || '');
      setTabletUrl(currentPage.settings.redirect?.tabletUrl || '');
    }
  }, [currentPage?.id, effectivePlan]); // Re-run when page or plan changes

  const handleAffiliatePostbackEnabledChange = React.useCallback(async (on: boolean) => {
    if (!currentPage?.id || affiliatePostbackSaving) return;

    const previous = affiliatePostbackEnabled;
    setAffiliatePostbackSaving(true);

    try {
      const authToken =
        token ||
        useAuthStore.getState().token ||
        localStorage.getItem('token') ||
        '';

      const res = await fetch(`${API_URL}/capi/affiliate-config/${currentPage.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ enabled: on }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setAffiliatePostbackEnabled(on);
      setCurrentPage({
        ...currentPage,
        settings: {
          ...currentPage.settings,
          affiliate: {
            ...((currentPage.settings as any).affiliate || {}),
            enabled: on,
          },
        } as any,
      });
    } catch (e) {
      setAffiliatePostbackEnabled(previous);
      console.warn('Failed to update affiliate postback:', e);
      toast.error(t('affiliatePostback.loadFailed'));
    } finally {
      setAffiliatePostbackSaving(false);
    }
  }, [
    affiliatePostbackEnabled,
    affiliatePostbackSaving,
    currentPage,
    setCurrentPage,
    t,
    token,
  ]);

  // Load the safe-page template catalog once. The pool is small and rarely
  // changes, so a single fetch on Builder mount is enough.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(`${API_URL}/safe-pages/templates`).then(r => r.ok ? r.json() : { templates: [] }),
      fetch(`${API_URL}/safe-pages/categories`).then(r => r.ok ? r.json() : { categories: [] }),
      fetch(`${API_URL}/safe-pages/niches`).then(r => r.ok ? r.json() : { niches: [] }),
    ]).then(([t, c, n]) => {
      if (cancelled) return;
      setSafePageCatalog({
        templates: t.templates || [],
        categories: c.categories || [],
        niches: n.niches || [],
      });
    }).catch(() => { /* silent — preview is best-effort */ });
    return () => { cancelled = true; };
  }, []);

  // Resolve "which template will bots actually see for THIS page" whenever
  // the page id or the user's hint changes. Server-side picker is the source
  // of truth (so the label can never drift from runtime behavior).
  useEffect(() => {
    if (!currentPage?.id || !cloakingEnabled) {
      setSafePagePick(null);
      return;
    }
    const params = new URLSearchParams({
      pageId: currentPage.id,
      hint: safePageTemplate || 'auto',
      niche: adNiche || 'general',
    });
    let cancelled = false;
    fetch(`${API_URL}/safe-pages/pick?${params.toString()}`)
      .then(r => r.ok ? r.json() : null)
      .then(json => { if (!cancelled && json?.pick) setSafePagePick(json.pick); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [currentPage?.id, safePageTemplate, adNiche, cloakingEnabled]);

  // Bot Cloaking Level 3 — load existing AI-generated variations for this page.
  useEffect(() => {
    if (!currentPage?.id || !cloakingEnabled || !token) {
      setAiSafePages({ exists: false, variations: [] });
      return;
    }
    let cancelled = false;
    fetch(`${API_URL}/safe-pages/ai/${currentPage.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (cancelled || !json) return;
        if (json.exists) {
          setAiSafePages({ exists: true, meta: json.meta, variations: json.variations || [] });
          // Pre-fill the input with whatever theme produced the current
          // article, so a "Regenerate" click visibly carries the same
          // hint forward (the user can still edit or clear it).
          if (typeof json.meta?.custom_topic === 'string') {
            setAiCustomTopic(json.meta.custom_topic);
          }
        } else {
          setAiSafePages({ exists: false, variations: [] });
        }
      })
      .catch(() => { /* silent — AI is optional */ });
    return () => { cancelled = true; };
  }, [currentPage?.id, cloakingEnabled, token]);

  const handleGenerateAiSafePages = async () => {
    if (!currentPage?.id || !token) return;
    // AI Safe Pages is Agency+ in the new pricing model. Server-side
    // requireFeature('aiSafePages') is the actual gate; this client-side
    // check just avoids round-tripping a 403 for an obvious case.
    if (!hasFeature(effectivePlan, 'aiSafePages')) {
      toast.error(t('advanced.cloaking.toast.aiProRequired'));
      return;
    }
    setIsGeneratingAi(true);
    setAiCustomTopicError(null);
    const toastId = toast.loading(t('advanced.cloaking.toast.aiGenerating'));
    try {
      // Persist current cloaking settings first so the server reads the same
      // niche/lang the user sees on screen. Without this, a brand-new niche
      // selection that hasn't been saved yet would silently fall back to the
      // 'general' default in the DB and produce off-topic content.
      const saved = await handleSave(false);
      if (!saved) {
        throw new Error('Could not save the page before generating. Try again.');
      }
      const trimmedCustomTopic = aiCustomTopic.trim();
      const res = await fetch(`${API_URL}/safe-pages/ai/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // Also send niche/lang explicitly — belt-and-braces against any race
        // between save and generate. Server prefers the body when present.
        body: JSON.stringify({
          pageId: currentPage.id,
          niche: adNiche,
          lang: aiSafePageLang,
          // Always send (empty string included) so the user can CLEAR
          // the theme by emptying the input and regenerating — server
          // reads the body unconditionally before falling back to the
          // persisted value.
          customTopic: trimmedCustomTopic,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        // 400 with reason "banned_term" → keep the input filled, surface
        // the explanation inline beneath the field so the user can edit
        // and try again. Toast still fires so they can't miss it.
        if (res.status === 400 && json?.reason === 'banned_term') {
          setAiCustomTopicError(json.message || 'Your topic contains terms that would defeat the cloak.');
        } else if (res.status === 400 && json?.reason === 'too_long') {
          setAiCustomTopicError(json.message || 'Topic is too long.');
        }
        throw new Error(json.message || json.detail || json.error || 'Generation failed');
      }
      toast.success(json.regenerated ? t('advanced.cloaking.toast.aiRegenerated') : t('advanced.cloaking.toast.aiGenerated'), { id: toastId });
      setAiSafePages({
        exists: true,
        meta: json.meta,
        variations: json.variations || [],
      });
      // Mirror the server's view of what topic produced this article.
      if (typeof json.meta?.custom_topic === 'string') {
        setAiCustomTopic(json.meta.custom_topic);
      }
      setAiVariationIndex(undefined);
    } catch (e: any) {
      toast.error(e?.message || t('advanced.cloaking.toast.aiFailed'), { id: toastId });
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const handleSave = async (showToast = true): Promise<boolean> => {
    try {
      // Update page settings with analytics, cloaking and redirect
      if (currentPage) {
        // Filter out empty Facebook Pixel IDs
        const validFacebookPixelIds = facebookPixelIds.filter(id => id.trim() !== '');
        
        // ✅ เก็บ backgroundColor, backgroundImage และ backgroundVideo ไว้ก่อน!
        const preservedSettings = {
          backgroundColor: currentPage.settings.backgroundColor,
          backgroundImage: currentPage.settings.backgroundImage,
          backgroundVideo: currentPage.settings.backgroundVideo,
        };
        
        setCurrentPage({
          ...currentPage,
          settings: {
            ...currentPage.settings,
            ...preservedSettings, // ✅ ใส่กลับคืน!
            analytics: {
              googleAnalyticsId: googleAnalyticsId.trim() || undefined,
              googleTagManagerId: googleTagManagerId.trim() || undefined,
              facebookPixelIds: validFacebookPixelIds,
              tiktokPixelId: tiktokPixelId.trim() || undefined,
              // Only persist metaCapi when at least one field is meaningful;
              // strip the key entirely when blank so the per-page override
              // disappears and the account-level default takes over.
              metaCapi: (() => {
                const tokenIds = (metaCapi.tokenIds || [])
                  .map((id) => String(id || '').trim())
                  .filter(Boolean);
                const hasTokenIds = tokenIds.length > 0;
                const hasLegacyTokenId = !!(metaCapi.tokenId && metaCapi.tokenId.trim());
                const hasAccessToken = !!(metaCapi.accessToken && metaCapi.accessToken.trim());
                const hasTestCode = !!(metaCapi.testEventCode && metaCapi.testEventCode.trim());
                // CAPI is now opt-in per page: any explicit enabled
                // value (true OR false) is meaningful and must round-
                // trip so the server can short-circuit dispatch when
                // the operator turns it off.
                const hasEnabledFlag = typeof metaCapi.enabled === 'boolean';

                if (!hasTokenIds && !hasLegacyTokenId && !hasAccessToken && !hasTestCode && !hasEnabledFlag) {
                  return undefined;
                }

                return {
                  ...(hasTokenIds ? { tokenIds } : {}),
                  // Carry legacy tokenId only if no new-style selection is present,
                  // so old pages keep working until next save.
                  ...(!hasTokenIds && hasLegacyTokenId
                    ? { tokenId: metaCapi.tokenId!.trim() }
                    : {}),
                  ...(hasAccessToken ? { accessToken: metaCapi.accessToken!.trim() } : {}),
                  ...(hasTestCode ? { testEventCode: metaCapi.testEventCode!.trim() } : {}),
                  ...(metaCapi.enabled !== undefined ? { enabled: metaCapi.enabled } : {}),
                };
              })(),
            },
            seo: {
              indexing: seoIndexing,
            },
            branding: {
              show: showBranding,
            },
            // Used by AI safe-page generation (GET/POST /safe-pages/ai/*).
            lang: aiSafePageLang,
            cloaking: {
              enabled: hasFeature(effectivePlan, 'cloaking') ? cloakingEnabled : false,
              safePageUrl: safePageUrl,
              safePageTemplate: safePageTemplate,
              adNiche: adNiche,
              aiVariationIndex: aiVariationIndex,
              allowedCountries: allowedCountries,
              allowedCities: allowedCities,
              autoBlockDatacenters: autoBlockDatacenters,
              blockedAsns: customBlockedAsns,
            },
            redirect: {
              enabled: redirectEnabled,
              desktopUrl: desktopUrl,
              mobileUrl: mobileUrl,
              tabletUrl: tabletUrl,
            },
          },
        });
      }
      
      await savePage();
      // Signal to children (DevDocShareCard, etc.) that the server
      // now has up-to-date state for this page. Cheaper than an
      // event bus; one source of truth lives here next to the save.
      setLastSavedAt(Date.now());
      if (showToast) {
        toast.dismiss();
        toast.success(t('toast.saveSuccess'));
      }
      return true;
    } catch (error: any) {
      console.error('Error saving page:', error);
      toast.dismiss();
      toast.error(error?.message || t('toast.saveFailed'));
      return false;
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      toast.dismiss();
      toast.success(t('toast.backTo', { step: t(`topbar.steps.${steps[currentStep - 2].key}`) }));
    }
  };

  const handleNextStep = async () => {
    const saveSuccess = await handleSave(false);

    if (saveSuccess && currentStep < 5) {
      setCurrentStep(currentStep + 1);
      toast.dismiss();
      toast.success(t('toast.movedTo', { step: t(`topbar.steps.${steps[currentStep].key}`) }));
    }
  };

  const handlePublishToDomain = async () => {
    if (!currentPage || isPublishing) return;
    
    setIsPublishing(true);
    
    try {
      console.log('🚀 Publishing page:', currentPage.id);
      
      // Set page as published
      updatePage({ published: true });
      
      // Save the page
      await savePage();
      
      console.log('✅ Page published successfully');
      
      toast.dismiss();
      toast.success(t('toast.publishSuccess'), {
        duration: 1500,
      });
      
      // Redirect to dashboard after showing toast
      console.log('🔄 Redirecting to dashboard in 1.5 seconds...');
      
      setTimeout(() => {
        console.log('🚀 Executing navigation to /dashboard');
        setIsPublishing(false);
        navigate('/dashboard');
      }, 1500);
    } catch (error: any) {
      console.error('❌ Publish error:', error);
      setIsPublishing(false);
      toast.error(error?.message || t('toast.publishFailed'));
    }
  };

  const _handleGetShareableLink = () => {
    if (!currentPage) return;
    
    // Use environment variable for app URL
    const shareableLink = `${APP_URL}/p/${currentPage.id}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareableLink)
      .then(() => {
        toast.dismiss();
        toast.success(t('toast.shareCopied'));
      })
      .catch(() => {
        toast.dismiss();
        toast.error(t('toast.shareFailed'));
      });
  };

  const _handleExportHTML = () => {
    if (!currentPage) return;

    // Generate HTML content
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${currentPage.settings.meta.title}</title>
  <meta name="description" content="${currentPage.settings.meta.description}">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: ${currentPage.settings.backgroundColor || '#ffffff'};
    }
  </style>
</head>
<body>
  <div id="landing-page">
    <!-- Components will be rendered here -->
    ${currentPage.components.map(comp => `<div data-component="${comp.type}">${comp.type}</div>`).join('\n    ')}
  </div>
</body>
</html>`;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentPage.name.replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.dismiss();
    toast.success(t('toast.exportSuccess'));
  };

  // Reference reserved helpers/values so noUnusedLocals doesn't reject them
  // while we keep the code around for future wiring.
  void _accountHasCapi;
  void _handleGetShareableLink;
  void _handleExportHTML;

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Top Navigation — stack + wrap on narrow viewports so actions never clip */}
      <div className="bg-gray-800 border-b border-gray-700 px-3 py-3 sm:px-4 sm:py-3 md:px-6 md:py-4">
        <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 md:gap-4 lg:gap-6">
            <h1
              className="shrink-0 text-base font-semibold text-white sm:text-lg cursor-pointer hover:text-gray-300 transition-colors truncate"
              onClick={() => navigate('/dashboard')}
            >
              {t('title')}
            </h1>

            {/*
              Steps strip — must stay on a single row.

              We previously used `flex-wrap`, which caused the 5th step
              ("เผยแพร่") to drop to a second row whenever the action
              cluster on the right grew (Save / Back / Next / lang
              switch / avatar). That made the topbar visually
              top-heavy at typical desktop widths.

              New behaviour:
                • flex-nowrap keeps everything on one row.
                • overflow-x-auto + thin scrollbar lets the user pan
                  horizontally if the viewport is genuinely too narrow
                  (rare on the desktop builder, common on tablets).
                • whitespace-nowrap on the label prevents the Thai
                  word "ตั้งค่าเว็บไซต์" from breaking mid-word.
                • Chevrons are hidden below md to save width.
                • Tighter px-2 / py-1 baseline and a smaller xs text
                  size shave ~80px off the strip at md, which is what
                  pushed it to two rows in the first place.
            */}
            <div className="flex min-w-0 flex-nowrap items-center gap-1 overflow-x-auto md:gap-2 [scrollbar-width:thin] [-ms-overflow-style:none]">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(step.number)}
                    className={`flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 transition-colors sm:py-1.5 md:px-3 md:py-2 ${
                      currentStep === step.number
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <div
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold sm:h-6 sm:w-6 sm:text-xs ${
                        currentStep === step.number
                          ? 'bg-white text-blue-600'
                          : 'bg-gray-700 text-gray-400'
                      }`}
                    >
                      {step.number}
                    </div>
                    <span className="whitespace-nowrap text-[11px] font-medium sm:text-xs md:text-sm">
                      {t(`topbar.steps.${step.key}`)}
                    </span>
                  </button>
                  {index < steps.length - 1 && (
                    <ChevronRight size={14} className="hidden shrink-0 text-gray-600 md:block" aria-hidden />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2 md:gap-3 lg:w-auto lg:shrink-0">
            <button
              type="button"
              onClick={() => handleSave()}
              disabled={isSaving}
              className="flex items-center gap-1.5 rounded-lg bg-gray-700 px-2.5 py-2 text-white transition-colors hover:bg-gray-600 disabled:opacity-50 sm:gap-2 sm:px-3 md:px-4"
            >
              <Save size={18} className="shrink-0" />
              <span className="text-xs font-medium sm:text-sm">
                {isSaving ? t('topbar.saving') : t('topbar.save')}
              </span>
            </button>

            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePrevStep}
                className="flex items-center gap-1.5 rounded-lg bg-gray-700 px-2.5 py-2 text-white transition-colors hover:bg-gray-600 sm:gap-2 sm:px-3 md:px-4"
              >
                <ChevronLeft size={18} className="shrink-0" />
                <span className="text-xs font-medium sm:text-sm">{t('topbar.previous')}</span>
              </button>
            )}

            {currentStep < 5 && (
              <button
                type="button"
                onClick={handleNextStep}
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-2.5 py-2 text-white transition-colors hover:bg-blue-700 sm:gap-2 sm:px-3 md:px-4"
              >
                <span className="text-xs font-medium sm:text-sm">{t('topbar.nextStep')}</span>
                <ChevronRight size={18} className="shrink-0" />
              </button>
            )}

            <LanguageSwitcher size="sm" variant="dark" className="shrink-0" />

            {user && (
              <div
                className="ml-0.5 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-sm font-semibold text-white sm:ml-1"
                onClick={() => navigate('/dashboard')}
                title={user.name}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') navigate('/dashboard');
                }}
              >
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {currentStep === 1 && (
          <>
            <ComponentSidebar
              isMobileOpen={componentsSidebarMobileOpen}
              onMobileClose={() => setComponentsSidebarMobileOpen(false)}
            />
            <Canvas />
            <SettingsPanel />

            {/* Mobile-only floating "+ Add" button. Below the lg
                breakpoint the ComponentSidebar is hidden by default to
                give the canvas the full viewport — this FAB opens it
                back as a slide-in drawer. We hide it when the drawer
                is already open to avoid a duplicate trigger sitting on
                top of the overlay. */}
            {!componentsSidebarMobileOpen && (
              <button
                type="button"
                onClick={() => setComponentsSidebarMobileOpen(true)}
                className="lg:hidden fixed bottom-5 left-5 z-30 flex items-center gap-2 rounded-full bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-2xl shadow-blue-900/40 hover:bg-blue-700 active:scale-95 transition-all"
                aria-label={t('topbar.steps.design')}
              >
                <Plus className="h-5 w-5" />
                <span>{t('topbar.steps.design')}</span>
              </button>
            )}
          </>
        )}

        {currentStep === 2 && (
          <div className="flex-1 overflow-y-auto bg-gray-800">
            <div className="max-w-2xl w-full mx-auto my-8 p-8 bg-gray-900 rounded-xl">
              <h2 className="text-2xl font-bold text-white mb-6">{t('pageSettings.title')}</h2>
              <div className="space-y-6">
                {/* Custom URL — moved to top so users can grab their link first */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('pageSettings.customUrl.label')}</label>
                  <div className="flex gap-3 items-center">
                    <div className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400">
                      {/* Always use subdomain structure (*.hitzlink.com) */}
                      https://{(currentPage as any)?.slug || currentPage?.id}.{BASE_DOMAIN}
                    </div>
                    <button
                      onClick={() => setShowSlugModal(true)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    >
                      {t('pageSettings.customUrl.edit')}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('pageSettings.customUrl.hint', { domain: BASE_DOMAIN })}
                  </p>
                </div>

                {/* Custom domains linked to THIS page. Filtered from the
                    full account list so the operator can see at a glance
                    that managolfshop.club serves this exact page —
                    without going to the Custom Domain page first.
                    Read-only here: assignment / removal lives in the
                    Custom Domain manager (linkage is set there at
                    add-time, can be reassigned via PATCH). */}
                {(() => {
                  const linked = currentPage
                    ? accountCustomDomains.filter((d) => d.pageId === currentPage.id)
                    : [];
                  if (linked.length === 0) {
                    return (
                      <div className="p-4 bg-gray-800/30 border border-dashed border-gray-700 rounded-lg">
                        <p className="text-sm text-gray-300 font-medium mb-1">
                          {t('pageSettings.customDomain.label')}
                        </p>
                        <p className="text-xs text-gray-500">
                          <Trans
                            i18nKey="pageSettings.customDomain.empty"
                            ns="builder"
                            components={{
                              link: (
                                <a
                                  href="/custom-domain"
                                  className="text-blue-400 hover:text-blue-300 underline"
                                />
                              ),
                            }}
                          />
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="p-4 bg-blue-900/20 border border-blue-800/50 rounded-lg">
                      <p className="text-sm text-gray-300 font-medium mb-3 flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-400" />
                        {t('pageSettings.customDomain.label')}
                      </p>
                      <div className="space-y-2">
                        {linked.map((d) => {
                          const isActive = d.status === 'active';
                          return (
                            <div
                              key={d.id}
                              className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-900/60 border border-gray-700 rounded-md"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <a
                                  href={isActive ? `https://${d.hostname}` : '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => { if (!isActive) e.preventDefault(); }}
                                  className={`font-mono text-sm truncate ${
                                    isActive
                                      ? 'text-blue-300 hover:text-blue-200 hover:underline'
                                      : 'text-gray-400 cursor-not-allowed'
                                  }`}
                                  title={isActive ? `https://${d.hostname}` : d.hostname}
                                >
                                  {d.hostname}
                                </a>
                                <span
                                  className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                                    isActive
                                      ? 'bg-green-900/50 text-green-300'
                                      : d.status === 'failed'
                                        ? 'bg-red-900/50 text-red-300'
                                        : 'bg-yellow-900/50 text-yellow-300'
                                  }`}
                                >
                                  {t(`pageSettings.customDomain.status.${d.status}`)}
                                </span>
                              </div>
                              <a
                                href="/custom-domain"
                                className="text-xs text-gray-400 hover:text-blue-300 whitespace-nowrap"
                              >
                                {t('pageSettings.customDomain.manage')}
                              </a>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-3">
                        {t('pageSettings.customDomain.hint')}
                      </p>
                    </div>
                  );
                })()}

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('pageSettings.pageTitle')}</label>
                  <input
                    type="text"
                    value={currentPage?.settings.meta.title || ''}
                    onChange={(e) => {
                      if (!currentPage) return;
                      const newTitle = e.target.value;
                      setCurrentPage({
                        ...currentPage,
                        name: newTitle,
                        settings: {
                          ...currentPage.settings,
                          meta: { 
                            ...currentPage.settings.meta, 
                            title: newTitle 
                          },
                        },
                      });
                    }}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('pageSettings.metaDescription')}</label>
                  <textarea
                    value={currentPage?.settings.meta.description || ''}
                    onChange={(e) => {
                      if (!currentPage) return;
                      setCurrentPage({
                        ...currentPage,
                        settings: {
                          ...currentPage.settings,
                          meta: { 
                            ...currentPage.settings.meta, 
                            description: e.target.value 
                          },
                        },
                      });
                    }}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                {/* Search Engine Indexing — moved here from Advanced (step 3)
                    so all SEO-facing toggles live with Title / Description. */}
                <div className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
                  <ToggleSwitch
                    label={t('pageSettings.seo.label')}
                    checked={seoIndexing}
                    onChange={setSeoIndexing}
                    description={t('pageSettings.seo.description')}
                  />
                  <div className="mt-2 p-3 bg-gray-900/60 rounded-lg">
                    <p className="text-xs text-gray-400 flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">ℹ️</span>
                      <span>
                        <strong className="text-gray-300">{t('pageSettings.seo.enabledLabel')}</strong> {t('pageSettings.seo.enabledHint')}<br />
                        <strong className="text-gray-300 mt-1 inline-block">{t('pageSettings.seo.disabledLabel')}</strong> {t('pageSettings.seo.disabledHint')}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Page Layout Width — simplified to Mobile vs Full width.
                    maxPageWidth is left at the persisted value (default 480)
                    and is no longer user-tunable to keep the UI clean. */}
                <div className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-white mb-1">
                      {t('pageSettings.layout.title')}
                    </label>
                    <p className="text-xs text-gray-400">
                      {t('pageSettings.layout.description')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {([
                      { value: 'mobile', label: t('pageSettings.layout.mobileLabel'), emoji: '📱', desc: t('pageSettings.layout.mobileDesc') },
                      { value: 'fluid', label: t('pageSettings.layout.fluidLabel'), emoji: '🖥️', desc: t('pageSettings.layout.fluidDesc') },
                    ] as const).map((opt) => {
                      const current = currentPage?.settings.layoutWidth ?? 'mobile';
                      const active = current === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            if (!currentPage) return;
                            setCurrentPage({
                              ...currentPage,
                              settings: {
                                ...currentPage.settings,
                                layoutWidth: opt.value,
                              },
                            });
                          }}
                          className={`text-left p-3 rounded-lg border transition-all ${
                            active
                              ? 'bg-blue-600/20 border-blue-500 ring-2 ring-blue-500/40'
                              : 'bg-gray-900 border-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <div className="text-lg">{opt.emoji}</div>
                          <div className="text-sm font-semibold text-white">{opt.label}</div>
                          <div className="text-xs text-gray-400">{opt.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Favicon Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('pageSettings.favicon.label')}</label>
                  <div className="space-y-3">
                    {currentPage?.settings.meta.favicon && (
                      <div className="p-3 bg-gray-800 rounded-lg border border-gray-700 space-y-3">
                        <div className="flex items-center gap-3">
                          <img 
                            src={currentPage.settings.meta.favicon} 
                            alt={t('pageSettings.favicon.alt')} 
                            className="w-10 h-10 rounded bg-gray-700"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"><rect width="32" height="32" fill="%23374151"/><text x="16" y="20" text-anchor="middle" fill="%239CA3AF" font-size="12">?</text></svg>';
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-400 font-mono truncate">{currentPage.settings.meta.favicon}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (!currentPage) return;
                            if (!confirm(t('pageSettings.favicon.deleteConfirm'))) return;
                            setCurrentPage({
                              ...currentPage,
                              settings: {
                                ...currentPage.settings,
                                meta: { 
                                  ...currentPage.settings.meta, 
                                  favicon: undefined 
                                },
                              },
                            });
                            toast.success(t('pageSettings.favicon.removed'));
                          }}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 hover:text-red-300 rounded-lg text-sm font-medium transition-colors border border-red-600/30"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {t('pageSettings.favicon.delete')}
                        </button>
                      </div>
                    )}
                    <label className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-700 border-2 border-dashed border-gray-600 hover:border-blue-500 rounded-lg text-gray-300 cursor-pointer transition-all">
                      <input
                        type="file"
                        accept=".ico,.png,.jpg,.jpeg,.svg"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file || !currentPage) return;

                          // Validate file size (2MB)
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error(t('pageSettings.favicon.tooLarge'));
                            return;
                          }

                          // Validate file type
                          const allowedTypes = ['image/x-icon', 'image/png', 'image/jpeg', 'image/svg+xml'];
                          if (!allowedTypes.includes(file.type)) {
                            toast.error(t('pageSettings.favicon.invalidType'));
                            return;
                          }

                          const formData = new FormData();
                          formData.append('favicon', file);

                          try {
                            toast.loading(t('pageSettings.favicon.uploading'));
                            
                            const response = await fetch(`${API_URL}/pages/${currentPage.id}/upload-favicon`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                              },
                              body: formData,
                            });

                            if (!response.ok) {
                              const error = await response.json();
                              throw new Error(error.error || 'Upload failed');
                            }

                            const data = await response.json();
                            
                            // Update current page with new favicon URL
                            setCurrentPage({
                              ...currentPage,
                              settings: {
                                ...currentPage.settings,
                                meta: { 
                                  ...currentPage.settings.meta, 
                                  favicon: data.url 
                                },
                              },
                            });

                            toast.dismiss();
                            toast.success(t('pageSettings.favicon.uploadSuccess'));
                          } catch (error: any) {
                            toast.dismiss();
                            toast.error(error.message || t('pageSettings.favicon.uploadFailed'));
                            console.error('Favicon upload error:', error);
                          }

                          // Reset file input
                          e.target.value = '';
                        }}
                      />
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm font-medium">
                        {currentPage?.settings.meta.favicon ? t('pageSettings.favicon.change') : t('pageSettings.favicon.upload')}
                      </span>
                    </label>
                    <p className="text-xs text-gray-500">
                      {t('pageSettings.favicon.hint')}
                    </p>
                  </div>
                </div>

                {/* Branding Watermark — removable on any paid tier
                    (Pro/Agency/Enterprise). Free tier has it forced.
                    Enterprise also gets full white-label, but the toggle
                    surface is the same. */}
                <div className="pt-4 border-t border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-300">
                      {t('pageSettings.branding.label')}
                    </label>
                    {!isPaidTier(effectivePlan) && (
                      <span className="text-xs px-2 py-1 bg-blue-600 rounded-full">PRO</span>
                    )}
                  </div>
                  
                  {isPaidTier(effectivePlan) ? (
                    <>
                      <ToggleSwitch
                        label=""
                        checked={!showBranding}
                        onChange={(checked) => setShowBranding(!checked)}
                        description={t('pageSettings.branding.proHint')}
                      />
                      <div className="mt-2 p-3 bg-gray-800/50 rounded-lg">
                        <p className="text-xs text-gray-400 flex items-start gap-2">
                          <span className="text-green-400 mt-0.5">✓</span>
                          <span>
                            <strong className="text-gray-300">{t('pageSettings.branding.proLabel')}</strong> {t('pageSettings.branding.proDesc')}
                          </span>
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="p-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg border border-blue-500/30">
                      <div className="flex items-start gap-3">
                        <Crown className="text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
                        <div>
                          <h4 className="font-semibold text-white mb-1">{t('pageSettings.branding.lockedTitle')}</h4>
                          <p className="text-sm text-gray-300 mb-2">
                            {t('pageSettings.branding.lockedDesc')}
                          </p>
                          <button
                            onClick={() => navigate('/upgrade')}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-purple-700 transition-all"
                          >
                            {t('pageSettings.branding.upgradeCta')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="flex-1 overflow-y-auto bg-gray-800">
            <div className="max-w-2xl mx-auto p-8">
              <h2 className="text-3xl font-bold text-white mb-2">{t('trackingStep.title')}</h2>
              <p className="text-gray-400 mb-8">{t('trackingStep.subtitle')}</p>
              
              <div className="space-y-8">
                {/* Analytics Section — each provider is wrapped in a
                    collapsible TrackingSection so the page stays scannable.
                    Order is intentional: Pixel-first because that's what
                    most operators configure first, then CAPI (gated by an
                    enable toggle), then TikTok / GA / GTM, then the
                    operator-specific helpers (postback + destination-side
                    Dev Doc share). */}
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <BarChart3 size={20} className="text-blue-400" />
                    {t('advanced.analytics.title')}
                  </h3>
                  <div className="space-y-3">
                    {/* 1. Facebook Pixel — Picker over the operator's
                        Pixel Manager inventory. We deliberately removed
                        the free-form "type the ID" input here so the
                        Pixel Manager remains the single source of truth
                        for pixel registration (one place to add/promote/
                        rotate, no drift between pages). The picker shows
                        the union of:
                          (a) pixels in the Pixel Manager inventory, AND
                          (b) pixels currently selected on this page
                        so legacy pages with a pixel that's no longer in
                        the inventory don't silently lose tracking. */}
                    <TrackingSection
                      icon={<FacebookBrandIcon />}
                      title={t('advanced.analytics.facebookPixel.title')}
                      description={t('advanced.analytics.facebookPixel.description')}
                      mode="toggle"
                      enabled={fbPixelEnabled}
                      onEnabledChange={(on) => {
                        setFbPixelEnabled(on);
                        if (!on) setFacebookPixelIds([]);
                      }}
                      active={fbPixelEnabled && facebookPixelIds.some(id => id.trim() !== '')}
                    >
                      {(() => {
                        const isFreeUser = !isPaidTier(effectivePlan);
                        const selectedSet = new Set(
                          facebookPixelIds.map(id => id.trim()).filter(Boolean)
                        );
                        const inventoryIds = accountPixels.map(p => p.pixelId);
                        // Render union: inventory pixels first (in order),
                        // then any "orphan" pixels that are still selected
                        // but no longer present in the Pixel Manager.
                        const inventorySet = new Set(inventoryIds);
                        const orphanIds = Array.from(selectedSet).filter(
                          id => !inventorySet.has(id),
                        );
                        const hasAny = inventoryIds.length + orphanIds.length > 0;

                        // Toggle selection. For free users we behave
                        // single-select (radio-like) so they can never
                        // end up with multiple pixels selected — that
                        // matched the previous UX where only the first
                        // text input was editable on Free.
                        const toggle = (id: string) => {
                          const next = new Set(selectedSet);
                          if (next.has(id)) {
                            next.delete(id);
                          } else {
                            if (isFreeUser) next.clear();
                            next.add(id);
                          }
                          setFacebookPixelIds(Array.from(next));
                        };

                        const goToManager = () => navigate('/pixel-manager');

                        return (
                          <>
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                <span>{t('advanced.analytics.facebookPixel.pickerHelp')}</span>
                                <InfoTooltip side="left" widthClassName="w-72">
                                  <strong className="text-white">{t('advanced.analytics.facebookPixel.tooltipTitle')}</strong>
                                  <br />
                                  {t('advanced.analytics.facebookPixel.tooltipBody')}
                                </InfoTooltip>
                              </div>
                              <button
                                type="button"
                                onClick={goToManager}
                                className="shrink-0 inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                              >
                                <Target size={12} />
                                {t('advanced.analytics.facebookPixel.managerLink')}
                              </button>
                            </div>

                            {accountPixelsLoading ? (
                              <div className="flex items-center justify-center gap-2 px-4 py-6 rounded-lg border border-gray-700 bg-gray-800/50 text-sm text-gray-400">
                                <Loader2 size={14} className="animate-spin" />
                                <span>{t('advanced.analytics.facebookPixel.loading')}</span>
                              </div>
                            ) : !hasAny ? (
                              <div className="rounded-lg border border-dashed border-gray-700 bg-gray-800/40 p-5 text-center">
                                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-blue-500/10 flex items-center justify-center">
                                  <Target size={18} className="text-blue-400" />
                                </div>
                                <h4 className="text-sm font-semibold text-white mb-1">
                                  {t('advanced.analytics.facebookPixel.empty.title')}
                                </h4>
                                <p className="text-xs text-gray-400 mb-3 max-w-sm mx-auto leading-relaxed">
                                  {t('advanced.analytics.facebookPixel.empty.subtitle')}
                                </p>
                                <button
                                  type="button"
                                  onClick={goToManager}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                  <Plus size={14} />
                                  {t('advanced.analytics.facebookPixel.empty.cta')}
                                  <ArrowRight size={14} />
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide">
                                    {t('advanced.analytics.facebookPixel.pickLabel', { count: inventoryIds.length })}
                                  </label>
                                  <div className="flex items-center gap-2 text-[11px]">
                                    {!isFreeUser && (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => setFacebookPixelIds(inventoryIds)}
                                          className="text-blue-400 hover:text-blue-300"
                                        >
                                          {t('advanced.analytics.facebookPixel.selectAll')}
                                        </button>
                                        <span className="text-gray-600">·</span>
                                      </>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => setFacebookPixelIds([])}
                                      className="text-gray-400 hover:text-gray-300"
                                    >
                                      {t('advanced.analytics.facebookPixel.clear')}
                                    </button>
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  {accountPixels.map((p) => {
                                    const isOn = selectedSet.has(p.pixelId);
                                    return (
                                      <label
                                        key={p.pixelId}
                                        className={
                                          'flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ' +
                                          (isOn
                                            ? 'border-blue-500 bg-blue-500/10'
                                            : 'border-gray-700 bg-gray-800/60 hover:border-gray-600')
                                        }
                                      >
                                        <input
                                          type={isFreeUser ? 'radio' : 'checkbox'}
                                          name={isFreeUser ? 'fb-pixel-pick' : undefined}
                                          checked={isOn}
                                          onChange={() => toggle(p.pixelId)}
                                          className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900"
                                        />
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <code className="text-sm font-mono font-semibold text-white truncate">
                                              {p.pixelId}
                                            </code>
                                            {p.role === 'primary' && (
                                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-medium uppercase tracking-wide">
                                                {t('advanced.analytics.facebookPixel.role.primary')}
                                              </span>
                                            )}
                                            {p.role === 'mixed' && (
                                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-medium uppercase tracking-wide">
                                                {t('advanced.analytics.facebookPixel.role.mixed')}
                                              </span>
                                            )}
                                          </div>
                                          <div className="text-[11px] text-gray-400 mt-0.5">
                                            {t('advanced.analytics.facebookPixel.usedOn', { count: p.usedOnPages })}
                                          </div>
                                        </div>
                                      </label>
                                    );
                                  })}

                                  {/* Orphan rows — selected on this page but
                                      no longer in the Pixel Manager inventory.
                                      We keep them visible so the user can see
                                      AND uncheck them; they're flagged so it's
                                      clear they don't appear in the manager. */}
                                  {orphanIds.map((id) => (
                                    <label
                                      key={id}
                                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-yellow-600/40 bg-yellow-500/5 cursor-pointer transition-colors hover:border-yellow-500"
                                    >
                                      <input
                                        type={isFreeUser ? 'radio' : 'checkbox'}
                                        name={isFreeUser ? 'fb-pixel-pick' : undefined}
                                        checked
                                        onChange={() => toggle(id)}
                                        className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900"
                                      />
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <code className="text-sm font-mono font-semibold text-white truncate">
                                            {id}
                                          </code>
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 font-medium uppercase tracking-wide">
                                            {t('advanced.analytics.facebookPixel.role.notInManager')}
                                          </span>
                                        </div>
                                        <div className="text-[11px] text-gray-400 mt-0.5">
                                          {t('advanced.analytics.facebookPixel.notInManagerHint')}
                                        </div>
                                      </div>
                                    </label>
                                  ))}
                                </div>

                                <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                                  {isFreeUser
                                    ? t('advanced.analytics.facebookPixel.freeHint')
                                    : t('advanced.analytics.facebookPixel.proHint')}
                                </p>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </TrackingSection>

                    {/* 2. Meta Conversions API (CAPI) — header-level
                        toggle gates the selector. CAPI defaults to OFF
                        so a freshly created page never fires server-
                        side events until the operator explicitly opts
                        in. Flipping the switch on persists
                        enabled=true; flipping off persists
                        enabled=false and silences server events.

                        The CAPI-adjacent tool (Affiliate Postback) is
                        nested INSIDE this section as a collapsible
                        sub-card so the Analytics page stays focused —
                        it only shows up when the operator opens it. */}
                    <TrackingSection
                      icon={<MetaBrandIcon />}
                      title={t('advanced.analytics.capi.title')}
                      description={t('advanced.analytics.capi.description')}
                      mode="toggle"
                      enabled={
                        // Locked tiers can never appear "on" — even if
                        // the stored value somehow says enabled (e.g.
                        // user downgraded after enabling on Agency),
                        // the toggle UI must reflect "off" so the user
                        // doesn't think CAPI is firing when it isn't.
                        hasFeature(effectivePlan, 'metaCapi') &&
                        metaCapi.enabled === true
                      }
                      onEnabledChange={(on) => {
                        // Free/Pro can't enable — bounce them to the
                        // upgrade page instead of silently writing a
                        // value the server will reject anyway. Server
                        // also has checkMetaCapiAccess as the real gate.
                        if (on && !hasFeature(effectivePlan, 'metaCapi')) {
                          navigate('/upgrade');
                          return;
                        }
                        setMetaCapi({ ...metaCapi, enabled: on });
                      }}
                      active={
                        hasFeature(effectivePlan, 'metaCapi') &&
                        metaCapi.enabled === true &&
                        ((Array.isArray(metaCapi.tokenIds) && metaCapi.tokenIds.length > 0) ||
                          !!metaCapi.tokenId ||
                          !!(metaCapi.accessToken && metaCapi.accessToken.trim()))
                      }
                    >
                      {!hasFeature(effectivePlan, 'metaCapi') ? (
                        // Locked card replaces the entire body for
                        // non-Agency tiers. Mirrors the cloaking lock
                        // pattern — explains the value, lists what
                        // they unlock, drives them to /upgrade.
                        <div className="p-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg border border-blue-500/30">
                          <div className="flex items-start gap-3 mb-3">
                            <MetaBrandIcon />
                            <div>
                              <h4 className="font-semibold text-white mb-1">
                                {t('advanced.analytics.capi.lockedTitle', {
                                  defaultValue: 'Server-side Meta CAPI (Agency+)',
                                })}
                              </h4>
                              <p className="text-sm text-gray-300">
                                {t('advanced.analytics.capi.lockedDesc', {
                                  defaultValue:
                                    'Server-to-server Meta Conversions API — bypass ad-blockers and iOS 14.5 limits with deterministic event_id dedup. Available on Agency and Enterprise plans.',
                                })}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => navigate('/upgrade')}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm"
                          >
                            {t('advanced.analytics.capi.lockedCta', {
                              defaultValue: 'Upgrade to Agency to enable Meta CAPI',
                            })}
                          </button>
                        </div>
                      ) : null}
                      {hasFeature(effectivePlan, 'metaCapi') && (
                      <>
                      <div className="space-y-3">
                        {/* Inline help — explains what CAPI does + where
                            global settings (default token, custom token,
                            test event code) actually live. */}
                        <div className="flex items-start gap-2 text-xs text-gray-400 leading-relaxed">
                          <span>
                            <Trans
                              i18nKey="advanced.analytics.capi.intro"
                              ns="builder"
                              components={{ strong: <span className="font-medium text-gray-200" /> }}
                            />
                          </span>
                          <InfoTooltip side="left" widthClassName="w-72">
                            <strong className="text-white">{t('advanced.analytics.capi.tooltipTitle')}</strong>
                            <br />
                            {t('advanced.analytics.capi.tooltipBody')}
                          </InfoTooltip>
                        </div>

                        <PageMetaCapiSelector
                          compact
                          value={metaCapi}
                          onChange={setMetaCapi}
                          accountTokens={accountTokens}
                          accountDefaultTokenId={accountDefaultTokenId}
                          defaultPixelId={facebookPixelIds.find(id => id.trim()) || ''}
                        />

                        {/*
                          Destination-side integration:

                          We expose ONE prominent action — share a public
                          Integration Guide URL — so operators don't have to
                          paste 4 different snippets to the destination
                          site's dev team. The S2S Postback configuration
                          itself was promoted out of this section to its
                          own top-level TrackingSection below (sibling to
                          TikTok / GA / GTM) AND a dedicated /tracker-postback
                          page in the sidebar — it's CAPI-adjacent but
                          deserves the same prominence as the other
                          tracking providers.
                        */}
                        {currentPage?.id && (
                          <DevDocShareCard
                            pageId={currentPage.id}
                            savedAt={lastSavedAt}
                          />
                        )}
                      </div>
                      </>
                      )}
                    </TrackingSection>

                    {/* 3. TikTok Pixel */}
                    <TrackingSection
                      icon={<TikTokBrandIcon />}
                      title={t('advanced.analytics.tiktok.title')}
                      description={t('advanced.analytics.tiktok.description')}
                      mode="toggle"
                      enabled={tiktokEnabled}
                      onEnabledChange={(on) => {
                        setTiktokEnabled(on);
                        if (!on) setTiktokPixelId('');
                      }}
                      active={tiktokEnabled && tiktokPixelId.trim() !== ''}
                    >
                      <input
                        type="text"
                        value={tiktokPixelId}
                        onChange={(e) => setTiktokPixelId(e.target.value)}
                        placeholder="XXXXXXXXXXXXXXXXXXXX"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        {t('advanced.analytics.tiktok.hint')}
                      </p>
                    </TrackingSection>

                    {/* 4. Google Analytics */}
                    <TrackingSection
                      icon={<GoogleAnalyticsBrandIcon />}
                      title={t('advanced.analytics.ga.title')}
                      description={t('advanced.analytics.ga.description')}
                      mode="toggle"
                      enabled={gaEnabled}
                      onEnabledChange={(on) => {
                        setGaEnabled(on);
                        if (!on) setGoogleAnalyticsId('');
                      }}
                      active={gaEnabled && googleAnalyticsId.trim() !== ''}
                    >
                      <input
                        type="text"
                        value={googleAnalyticsId}
                        onChange={(e) => setGoogleAnalyticsId(e.target.value)}
                        placeholder="G-XXXXXXXXXX or UA-XXXXXXXXX-X"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        <Trans
                          i18nKey="advanced.analytics.ga.hint"
                          ns="builder"
                          components={{ code: <code className="text-blue-300" /> }}
                        />
                      </p>
                    </TrackingSection>

                    {/* 5. Google Tag Manager — gives the operator full
                        control of any tags they want to deploy via GTM
                        UI (extra GA4 events, server-side GTM, custom
                        pixels, etc) without re-deploying the page. */}
                    <TrackingSection
                      icon={<GoogleTagManagerBrandIcon />}
                      title={t('advanced.analytics.gtm.title')}
                      description={t('advanced.analytics.gtm.description')}
                      mode="toggle"
                      enabled={gtmEnabled}
                      onEnabledChange={(on) => {
                        setGtmEnabled(on);
                        if (!on) setGoogleTagManagerId('');
                      }}
                      active={gtmEnabled && googleTagManagerId.trim() !== ''}
                    >
                      <input
                        type="text"
                        value={googleTagManagerId}
                        onChange={(e) => setGoogleTagManagerId(e.target.value)}
                        placeholder="GTM-XXXXXXX"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        <Trans
                          i18nKey="advanced.analytics.gtm.hint"
                          ns="builder"
                          components={{ code: <code className="text-blue-300" /> }}
                        />
                      </p>

                      {/* Phase 3: auto-sync HitzLink's tag template
                          directly into the operator's GTM workspace
                          via API. Inline so the operator stays in
                          one workflow: type GTM ID → connect Google
                          (once, on Meta CAPI page) → pick container →
                          Sync. The panel self-hides until the page
                          has an ID and silently no-ops if the server
                          doesn't have GOOGLE_OAUTH_* env vars set. */}
                      {currentPage?.id && gtmEnabled && (
                        <GtmAutoSyncPanel
                          pageId={currentPage.id}
                          gtmIdHint={googleTagManagerId}
                          onSaveBeforeSync={() => handleSave(false)}
                        />
                      )}
                    </TrackingSection>

                    {/* 6. Affiliate S2S Postback — kept at the bottom of
                        the tracking list because it is the destination-
                        side conversion bridge. The row toggle writes the
                        same settings.affiliate.enabled flag used by the
                        inbound postback handler; the embedded panel hides
                        its duplicate enable switch. */}
                    {currentPage?.id && (
                      <TrackingSection
                        icon={
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-purple-500/20 text-purple-300 text-[10px] font-bold tracking-wider">
                            S2S
                          </span>
                        }
                        title={t('advanced.analytics.postback.title')}
                        description={t('advanced.analytics.postback.description')}
                        mode="toggle"
                        enabled={affiliatePostbackEnabled}
                        onEnabledChange={(on) => void handleAffiliatePostbackEnabledChange(on)}
                        active={affiliatePostbackEnabled}
                      >
                        <div className="flex items-start gap-2 text-xs text-gray-400 mb-3 leading-relaxed">
                          <span>
                            <Trans
                              i18nKey="advanced.analytics.postback.intro"
                              ns="builder"
                              components={{ em: <em /> }}
                            />
                          </span>
                          <InfoTooltip side="left" widthClassName="w-72">
                            <strong className="text-white">{t('advanced.analytics.postback.tooltipTitle')}</strong>
                            <br />
                            {t('advanced.analytics.postback.tooltipBody')}
                          </InfoTooltip>
                        </div>
                        <AffiliatePostbackPanel
                          pageId={currentPage.id}
                          embedded
                          hideEnableControl
                        />
                      </TrackingSection>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="flex-1 overflow-y-auto bg-gray-800">
            <div className="max-w-2xl mx-auto p-8">
              <h2 className="text-3xl font-bold text-white mb-2">{t('protectionStep.title')}</h2>
              <p className="text-gray-400 mb-8">{t('protectionStep.subtitle')}</p>

              <div className="space-y-8">
                {/* Bot Cloaking Section — Agency+ in the new pricing
                    model (PLAN_LIMITS.features.cloaking). Server-side
                    checkCloakingAccess() in routes/pages.js is the
                    real gate; the badge is rendered for any tier
                    without the entitlement so they see the upsell. */}
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Shield size={20} className="text-purple-400" />
                    {t('advanced.cloaking.title')} {!hasFeature(effectivePlan, 'cloaking') && <span className="text-xs px-2 py-1 bg-blue-600 rounded-full">AGENCY</span>}
                  </h3>
                  
                  {!hasFeature(effectivePlan, 'cloaking') ? (
                    <div className="p-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg border border-blue-500/30">
                      <div className="flex items-start gap-3 mb-3">
                        <Shield className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
                        <div>
                          <h4 className="font-semibold text-white mb-1">{t('advanced.cloaking.lockedTitle')}</h4>
                          <p className="text-sm text-gray-300 mb-2">
                            {t('advanced.cloaking.lockedDesc')}
                          </p>
                          <ul className="text-sm text-gray-400 space-y-1 ml-4 list-disc">
                            <li>{t('advanced.cloaking.lockedFeatures.f1')}</li>
                            <li>{t('advanced.cloaking.lockedFeatures.f2')}</li>
                            <li>{t('advanced.cloaking.lockedFeatures.f3')}</li>
                            <li>{t('advanced.cloaking.lockedFeatures.f4')}</li>
                          </ul>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate('/upgrade')}
                        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm"
                      >
                        {t('advanced.cloaking.lockedCta')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <ToggleSwitch
                          label={t('advanced.cloaking.enableLabel')}
                          checked={cloakingEnabled}
                          onChange={setCloakingEnabled}
                          description={t('advanced.cloaking.enableDesc')}
                        />
                        <span className="text-xs px-2 py-1 bg-purple-600 rounded-full text-white font-medium">AGENCY</span>
                      </div>
                      
                      {cloakingEnabled && (() => {
                        const hasCustomUrl = !!safePageUrl.trim();
                        return (
                        <div className="ml-4 pl-4 border-l-2 border-blue-500 space-y-4">
                          {/* 1. Ad Niche — drives topic-aligned safe pages */}
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              {t('advanced.cloaking.niche.label')} <span className="text-gray-500">{t('advanced.cloaking.niche.labelHint')}</span>
                            </label>
                            <select
                              value={adNiche}
                              onChange={(e) => setAdNiche(e.target.value)}
                              disabled={hasCustomUrl}
                              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {(safePageCatalog.niches.length > 0
                                ? safePageCatalog.niches
                                : [{ id: 'general', label: 'General', templateCount: 0, description: undefined as string | undefined }]
                              ).map(n => (
                                <option key={n.id} value={n.id}>
                                  {n.label}{n.templateCount ? t('advanced.cloaking.niche.templatesSuffix', { value: n.templateCount }) : ''}
                                </option>
                              ))}
                            </select>
                            {(() => {
                              const sel = safePageCatalog.niches.find(n => n.id === adNiche);
                              return sel?.description ? (
                                <p className="text-xs text-gray-500 mt-2 leading-relaxed">{sel.description}</p>
                              ) : null;
                            })()}
                          </div>

                          {/* 2a. AI-generated Safe Page (Level 3) —
                              Agency+ feature, N generations/page (cap
                              enforced server-side via PLAN_QUOTAS). */}
                          {!hasCustomUrl && (() => {
                            const isPro = hasFeature(effectivePlan, 'aiSafePages');
                            const aiVariation = aiSafePages.variations[0];
                            const hasAi = aiSafePages.exists && !!aiVariation;
                            return (
                              <div className="rounded-lg border border-purple-700/40 bg-gradient-to-br from-purple-950/30 to-gray-900/40 p-4">
                                <div className="flex items-start justify-between gap-3 mb-3">
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap">
                                      {t('advanced.cloaking.ai.title')}
                                      <span className="text-[10px] px-1.5 py-0.5 bg-purple-600/80 rounded text-white font-bold">{t('advanced.cloaking.ai.level')}</span>
                                      {!isPro && <span className="text-[10px] px-1.5 py-0.5 bg-yellow-600/80 rounded text-white font-bold">{t('advanced.cloaking.ai.proOnly')}</span>}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                                      {t('advanced.cloaking.ai.description')}
                                    </p>
                                  </div>
                                </div>

                                {/* Custom topic / theme hint — optional. Lets the
                                    user steer the article toward something that
                                    matches their ad creative (e.g. mythology,
                                    cultural folklore, design history) without
                                    breaking the cloak. The server pre-validates
                                    against the niche's banned-terms list and
                                    rejects anything that would defeat the cloak. */}
                                {isPro && (
                                  <div className="mb-4">
                                    <div className="flex flex-wrap items-center gap-2 mb-3">
                                      <span className="text-xs font-medium text-gray-300">{t('advanced.cloaking.ai.language')}</span>
                                      <div className="inline-flex rounded-lg border border-gray-700 overflow-hidden text-xs">
                                        <button
                                          type="button"
                                          onClick={() => setAiSafePageLang('en')}
                                          className={`px-3 py-1.5 font-medium transition-colors ${
                                            aiSafePageLang === 'en'
                                              ? 'bg-purple-600 text-white'
                                              : 'bg-gray-800/80 text-gray-400 hover:text-white'
                                          }`}
                                        >
                                          {tCommon('language.englishName')}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setAiSafePageLang('th')}
                                          className={`px-3 py-1.5 font-medium transition-colors border-l border-gray-700 ${
                                            aiSafePageLang === 'th'
                                              ? 'bg-purple-600 text-white'
                                              : 'bg-gray-800/80 text-gray-400 hover:text-white'
                                          }`}
                                        >
                                          {tCommon('language.thaiName')}
                                        </button>
                                      </div>
                                      <span className="text-[10px] text-gray-500">{t('advanced.cloaking.ai.languageNote')}</span>
                                    </div>
                                    <label className="block text-xs font-medium text-gray-300 mb-1.5 flex items-center gap-2">
                                      <span>🎯 {t('advanced.cloaking.ai.topicLabel')}</span>
                                      <span className="text-[10px] text-gray-500 font-normal">{t('advanced.cloaking.ai.topicHint')}</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={aiCustomTopic}
                                      onChange={(e) => {
                                        setAiCustomTopic(e.target.value);
                                        if (aiCustomTopicError) setAiCustomTopicError(null);
                                      }}
                                      maxLength={200}
                                      placeholder={(() => {
                                        const knownNiches = ['gambling', 'finance', 'health', 'crypto', 'loan', 'tech'] as const;
                                        const key = (knownNiches as readonly string[]).includes(adNiche)
                                          ? (adNiche as typeof knownNiches[number])
                                          : 'general';
                                        return t(`advanced.cloaking.ai.topicPlaceholder.${key}` as any);
                                      })()}
                                      className={`w-full px-3 py-2 bg-gray-800/80 border rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-2 transition-colors ${
                                        aiCustomTopicError
                                          ? 'border-red-500/60 focus:ring-red-500'
                                          : 'border-gray-700 focus:ring-purple-500 focus:border-purple-500'
                                      }`}
                                    />
                                    <div className="flex items-start justify-between gap-2 mt-1.5 text-[10px]">
                                      <p className={aiCustomTopicError ? 'text-red-400 leading-relaxed flex-1' : 'text-gray-500 leading-relaxed flex-1'}>
                                        {aiCustomTopicError ? (
                                          <>⚠️ {aiCustomTopicError}</>
                                        ) : (
                                          <Trans
                                            i18nKey="advanced.cloaking.ai.topicTip"
                                            ns="builder"
                                            components={{
                                              em: <em />,
                                              strong: <strong className="text-gray-400" />,
                                            }}
                                          />
                                        )}
                                      </p>
                                      <span className="text-gray-600 shrink-0">{aiCustomTopic.length}/200</span>
                                    </div>
                                  </div>
                                )}

                                {!hasAi && (
                                  <div className="space-y-3">
                                    <div className="text-xs text-gray-500 leading-relaxed">
                                      {t('advanced.cloaking.ai.oneGen')}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={handleGenerateAiSafePages}
                                      disabled={!isPro || isGeneratingAi}
                                      className="w-full px-4 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded-lg font-medium text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                      {isGeneratingAi ? (
                                        <>
                                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                          {t('advanced.cloaking.ai.generating')}
                                        </>
                                      ) : (
                                        <>{t('advanced.cloaking.ai.generate')}</>
                                      )}
                                    </button>
                                    {!isPro && (
                                      <p className="text-[11px] text-yellow-400/80 text-center">
                                        {t('advanced.cloaking.ai.upgradeHint')}
                                      </p>
                                    )}
                                  </div>
                                )}

                                {hasAi && (() => {
                                  const meta = aiSafePages.meta;
                                  const used = meta?.regenerations_used ?? 0;
                                  const max = meta?.regenerations_max ?? 1;
                                  const remaining = Math.max(0, max - used);
                                  const canRegenerate = remaining > 0;
                                  return (
                                  <div className="space-y-3">
                                    {/* Meta strip */}
                                    {meta && (
                                      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                                        <span>📅 {new Date(meta.generated_at).toLocaleDateString()}</span>
                                        <span>·</span>
                                        <span>{meta.lang.toUpperCase()}</span>
                                        <span>·</span>
                                        <span>{meta.input_tokens + meta.output_tokens} tokens (${meta.cost_usd.toFixed(4)})</span>
                                        {canRegenerate ? (
                                          <button
                                            type="button"
                                            onClick={handleGenerateAiSafePages}
                                            disabled={isGeneratingAi}
                                            className="ml-auto text-blue-400/80 hover:text-blue-300 underline disabled:opacity-40 disabled:cursor-not-allowed"
                                          >
                                            {isGeneratingAi ? t('advanced.cloaking.ai.regenerating') : t('advanced.cloaking.ai.regenerate', { remaining })}
                                          </button>
                                        ) : (
                                          <span className="ml-auto text-gray-500" title={t('advanced.cloaking.ai.lockedTitle')}>
                                            {t('advanced.cloaking.ai.lockedFinal')}
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Single variation card */}
                                    {(() => {
                                      const hero = aiVariation.images?.[0];
                                      const inlineImg = aiVariation.images?.[1];
                                      const imageCount = aiVariation.images?.length ?? 0;
                                      const usingUnsplash = aiVariation.images?.some((i) => i.source === 'unsplash');
                                      return (
                                    <div className="rounded-lg border border-purple-500/40 bg-purple-950/20 text-xs flex flex-col overflow-hidden">
                                      {hero && (
                                        <div className="relative aspect-[2/1] w-full bg-gray-900">
                                          <img
                                            src={hero.url}
                                            alt={hero.alt || aiVariation.title}
                                            className="absolute inset-0 w-full h-full object-cover"
                                            loading="lazy"
                                          />
                                          {imageCount > 0 && (
                                            <span
                                              className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 bg-black/60 text-white rounded backdrop-blur-sm"
                                              title={usingUnsplash ? t('advanced.cloaking.ai.unsplashTip') : t('advanced.cloaking.ai.stockTip')}
                                            >
                                              {usingUnsplash ? t('advanced.cloaking.ai.unsplash') : t('advanced.cloaking.ai.stock')} · {imageCount}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      <div className="p-3 flex flex-col gap-2">
                                        <div className="flex items-center justify-between gap-2">
                                          <span className="text-[10px] font-bold text-purple-400">{t('advanced.cloaking.ai.active')}</span>
                                          <button
                                            type="button"
                                            onClick={() => window.open(`${API_URL}/safe-pages/ai/preview/${currentPage?.id}/0?token=${token}`, '_blank', 'noopener')}
                                            className="text-[11px] text-blue-400 hover:text-blue-300"
                                          >
                                            {t('advanced.cloaking.ai.preview')}
                                          </button>
                                        </div>
                                        <div className="font-medium text-white leading-snug">
                                          {aiVariation.title}
                                        </div>
                                        <div className="text-gray-400 leading-snug line-clamp-3">
                                          {aiVariation.description}
                                        </div>
                                        {Array.isArray(aiVariation.tags) && aiVariation.tags.length > 0 && (
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {aiVariation.tags.slice(0, 6).map((tag) => (
                                              <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded">{tag}</span>
                                            ))}
                                          </div>
                                        )}
                                        {!hero && imageCount === 0 && (
                                          <div className="text-[10px] text-gray-500 italic mt-1">
                                            <Trans
                                              i18nKey="advanced.cloaking.ai.noImages"
                                              ns="builder"
                                              components={{ code: <code className="text-gray-400" /> }}
                                            />
                                          </div>
                                        )}
                                        {inlineImg && (
                                          <div className="text-[10px] text-gray-500 mt-1">
                                            {t('advanced.cloaking.ai.inlineImage')}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                      );
                                    })()}
                                  </div>
                                  );
                                })()}
                              </div>
                            );
                          })()}

                          {/* 2b. Static template fallback — only when no AI variations exist */}
                          {!hasCustomUrl && !aiSafePages.exists && (() => {
                            // Pool of templates that match the current niche.
                            // Falls back to the full pool when the niche has no
                            // dedicated templates — keeps Rotate useful even
                            // for "general".
                            const nicheTemplates = adNiche && adNiche !== 'general'
                              ? safePageCatalog.templates.filter(t => Array.isArray(t.niches) && t.niches.includes(adNiche))
                              : safePageCatalog.templates;
                            const pool = nicheTemplates.length > 0 ? nicheTemplates : safePageCatalog.templates;

                            const handleRotate = () => {
                              if (pool.length === 0) return;
                              const currentId = safePagePick?.id;
                              const currentIdx = pool.findIndex(t => t.id === currentId);
                              const nextIdx = currentIdx === -1
                                ? Math.floor(Math.random() * pool.length)
                                : (currentIdx + 1) % pool.length;
                              setSafePageTemplate(pool[nextIdx].id);
                            };

                            const isLocked = safePageTemplate && safePageTemplate !== 'auto';

                            return (
                              <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                  {t('advanced.cloaking.safePage.label')} <span className="text-gray-500">{t('advanced.cloaking.safePage.labelHint')}</span>
                                </label>
                                {safePagePick ? (
                                  <div className="p-3 bg-gray-800/60 rounded-lg border border-gray-700">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="text-xs text-gray-300 leading-relaxed min-w-0">
                                        <div className="text-gray-500 mb-1">{t('advanced.cloaking.safePage.botsSee')}</div>
                                        <div className="font-medium text-white truncate">{safePagePick.name}</div>
                                        <div className="text-gray-500 mt-1">
                                          {safePagePick.lang.toUpperCase()} · {safePagePick.category}
                                        </div>
                                      </div>
                                      <div className="shrink-0 flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={handleRotate}
                                          disabled={pool.length <= 1}
                                          title={pool.length <= 1 ? t('advanced.cloaking.safePage.rotateOnly') : t('advanced.cloaking.safePage.rotateHint')}
                                          className="px-3 py-1.5 text-xs font-medium text-purple-300 hover:text-white hover:bg-purple-600 border border-purple-600/40 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                          {t('advanced.cloaking.safePage.rotate')}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => window.open(`${API_URL}/safe-pages/preview/${safePagePick.id}`, '_blank', 'noopener')}
                                          className="px-3 py-1.5 text-xs font-medium text-blue-300 hover:text-white hover:bg-blue-600 border border-blue-600/40 rounded-lg transition-colors"
                                        >
                                          {t('advanced.cloaking.safePage.preview')}
                                        </button>
                                      </div>
                                    </div>
                                    {isLocked && (
                                      <div className="mt-2 pt-2 border-t border-gray-700/60 flex items-center justify-between gap-2 text-[11px]">
                                        <span className="text-gray-500">{t('advanced.cloaking.safePage.lockedNote')}</span>
                                        <button
                                          type="button"
                                          onClick={() => setSafePageTemplate('auto')}
                                          className="text-blue-400 hover:text-blue-300 font-medium"
                                        >
                                          {t('advanced.cloaking.safePage.resetAuto')}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="p-3 bg-gray-800/60 rounded-lg border border-gray-700 text-xs text-gray-500">
                                    {t('advanced.cloaking.safePage.loading')}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {hasCustomUrl && (
                            <div className="p-3 bg-amber-950/30 rounded-lg border border-amber-700/40">
                              <p className="text-xs text-amber-300 leading-relaxed">
                                {t('advanced.cloaking.safePage.customRedirect')}
                              </p>
                            </div>
                          )}

                          {/* 2c. Geo-fence — restrict money page to specific countries */}
                          <div className="pt-3 border-t border-gray-700">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              {t('advanced.cloaking.geo.label')} <span className="text-gray-500">{t('advanced.cloaking.geo.labelHint')}</span>
                            </label>

                            {/* Quick presets */}
                            <div className="flex flex-wrap gap-2 mb-2">
                              {COUNTRY_PRESETS.map(preset => {
                                const isActive =
                                  allowedCountries.length === preset.codes.length &&
                                  preset.codes.every(c => allowedCountries.includes(c));
                                return (
                                  <button
                                    key={preset.id}
                                    type="button"
                                    onClick={() => setAllowedCountries(preset.codes)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                      isActive
                                        ? 'bg-blue-600/20 border-blue-500 text-blue-300'
                                        : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:border-gray-600'
                                    }`}
                                  >
                                    {preset.label}
                                  </button>
                                );
                              })}
                              {allowedCountries.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setAllowedCountries([])}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium border bg-gray-800 border-gray-700 text-gray-400 hover:bg-red-900/20 hover:border-red-700 hover:text-red-300 transition-colors"
                                >
                                  {t('advanced.cloaking.geo.clear')}
                                </button>
                              )}
                            </div>

                            {/* Selected country chips */}
                            {allowedCountries.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-800/40 border border-gray-700 rounded-lg min-h-[44px]">
                                {allowedCountries.map(code => {
                                  const c = COUNTRY_BY_CODE[code];
                                  return (
                                    <span
                                      key={code}
                                      className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 bg-blue-600/20 border border-blue-500/40 rounded-md text-sm text-blue-100"
                                    >
                                      <span className="text-base leading-none">{c?.flag || '🏳️'}</span>
                                      <span className="font-medium">{c?.name || code}</span>
                                      <span className="text-xs text-blue-300/70 font-mono">({code})</span>
                                      <button
                                        type="button"
                                        onClick={() => setAllowedCountries(allowedCountries.filter(x => x !== code))}
                                        className="w-5 h-5 ml-0.5 rounded-full hover:bg-blue-500/40 text-blue-200 hover:text-white transition-colors flex items-center justify-center text-xs"
                                        aria-label={t('advanced.cloaking.geo.removeAria', { name: c?.name || code })}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            )}

                            {/* Add country dropdown */}
                            <select
                              value=""
                              onChange={(e) => {
                                const code = e.target.value;
                                if (code && !allowedCountries.includes(code)) {
                                  setAllowedCountries([...allowedCountries, code]);
                                }
                              }}
                              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">{t('advanced.cloaking.geo.addPlaceholder')}</option>
                              {Array.from(new Set(COUNTRIES.map(c => c.region))).map(region => (
                                <optgroup key={region} label={region}>
                                  {COUNTRIES
                                    .filter(c => c.region === region && !allowedCountries.includes(c.code))
                                    .map(c => (
                                      <option key={c.code} value={c.code}>
                                        {c.flag}  {c.name}  ({c.code})
                                      </option>
                                    ))}
                                </optgroup>
                              ))}
                            </select>

                            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                              <Trans
                                i18nKey="advanced.cloaking.geo.tip"
                                ns="builder"
                                components={{
                                  em: <em />,
                                  br: <br />,
                                  span: <span className="text-amber-400/80" />,
                                  code: <code className="text-gray-300" />,
                                }}
                              />
                            </p>
                          </div>

                          {/* 2d. City-level allow-list — runs AFTER country check passes */}
                          <div className="pt-3 border-t border-gray-700">
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              {t('advanced.cloaking.city.label')} <span className="text-gray-500">{t('advanced.cloaking.city.labelHint')}</span>
                            </label>

                            {/* Quick presets — Thai tier-1 cities */}
                            <div className="flex flex-wrap gap-2 mb-2">
                              {[
                                { id: 'bkk', label: t('advanced.cloaking.city.presets.bkk'), cities: ['Bangkok'] },
                                { id: 'th-tier1', label: t('advanced.cloaking.city.presets.thTier1'), cities: ['Bangkok', 'Chiang Mai', 'Phuket', 'Pattaya', 'Khon Kaen', 'Hat Yai'] },
                              ].map(preset => {
                                const isActive =
                                  allowedCities.length === preset.cities.length &&
                                  preset.cities.every(c => allowedCities.map(x => x.toLowerCase()).includes(c.toLowerCase()));
                                return (
                                  <button
                                    key={preset.id}
                                    type="button"
                                    onClick={() => setAllowedCities(preset.cities)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                      isActive
                                        ? 'bg-blue-600/20 border-blue-500 text-blue-300'
                                        : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:border-gray-600'
                                    }`}
                                  >
                                    {preset.label}
                                  </button>
                                );
                              })}
                              {allowedCities.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setAllowedCities([])}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium border bg-gray-800 border-gray-700 text-gray-400 hover:bg-red-900/20 hover:border-red-700 hover:text-red-300 transition-colors"
                                >
                                  {t('advanced.cloaking.city.clear')}
                                </button>
                              )}
                            </div>

                            {/* Selected city chips */}
                            {allowedCities.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-800/40 border border-gray-700 rounded-lg">
                                {allowedCities.map(city => (
                                  <span
                                    key={city}
                                    className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 bg-purple-600/20 border border-purple-500/40 rounded-md text-sm text-purple-100"
                                  >
                                    <span>📍</span>
                                    <span className="font-medium">{city}</span>
                                    <button
                                      type="button"
                                      onClick={() => setAllowedCities(allowedCities.filter(c => c !== city))}
                                      className="w-5 h-5 ml-0.5 rounded-full hover:bg-purple-500/40 text-purple-200 hover:text-white transition-colors flex items-center justify-center text-xs"
                                      aria-label={t('advanced.cloaking.city.removeAria', { city })}
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Free-text input — accepts any city name */}
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={cityInput}
                                onChange={(e) => setCityInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const trimmed = cityInput.trim();
                                    if (trimmed && !allowedCities.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
                                      setAllowedCities([...allowedCities, trimmed]);
                                    }
                                    setCityInput('');
                                  }
                                }}
                                placeholder={t('advanced.cloaking.city.placeholder')}
                                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const trimmed = cityInput.trim();
                                  if (trimmed && !allowedCities.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
                                    setAllowedCities([...allowedCities, trimmed]);
                                  }
                                  setCityInput('');
                                }}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                              >
                                {t('advanced.cloaking.city.add')}
                              </button>
                            </div>

                            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                              <Trans
                                i18nKey="advanced.cloaking.city.tip"
                                ns="builder"
                                components={{
                                  em: <em />,
                                  br: <br />,
                                  span: <span className="text-amber-400/80" />,
                                }}
                              />
                            </p>
                          </div>

                          {/* ASN block-list (datacenter / VPS / VPN auto-block)
                              has no UI here on purpose — the average operator
                              doesn't know what an ASN is, and the curated
                              default list is the single biggest win against
                              fake bot traffic. It's now always-on whenever
                              cloaking is enabled (see autoBlockDatacenters
                              const at the top of this component). */}

                          {/* 3. Advanced cloaking overrides — hidden by default to
                              keep the panel clean for the 95%+ of users who never
                              need them. Auto-expanded when the user has previously
                              configured a custom ASN or safe-page URL so we never
                              hide existing settings out from under them. */}
                          {(() => {
                            // Note: customBlockedAsns no longer triggers
                            // auto-expand because there's no UI to render
                            // for it any more — only the safe-page URL
                            // override remains in the advanced disclosure.
                            const hasAdvancedValues =
                              (safePageUrl ?? '').trim() !== '';
                            const expanded = advancedCloakingOpen || hasAdvancedValues;
                            return (
                              <div className="pt-3 border-t border-gray-700">
                                <button
                                  type="button"
                                  onClick={() => setAdvancedCloakingOpen(!expanded)}
                                  className="w-full flex items-center justify-between px-3 py-2 -mx-1 rounded-lg hover:bg-gray-800/50 transition-colors group"
                                  aria-expanded={expanded}
                                >
                                  <span className="text-sm font-medium text-gray-300 flex items-center gap-2">
                                    <span className="text-gray-500">⚙️</span>
                                    {t('advanced.cloaking.advancedDisclosure.label')}
                                    <span className="text-xs text-gray-500 font-normal">
                                      {t('advanced.cloaking.advancedDisclosure.hint')}
                                    </span>
                                  </span>
                                  <ChevronDown
                                    size={18}
                                    className={`text-gray-500 group-hover:text-gray-300 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                                  />
                                </button>

                                {expanded && (
                                  <div className="mt-3 space-y-4 pl-1">
                                    {/* Custom ASN block-list UI removed —
                                        legacy `customBlockedAsns` values
                                        still round-trip via state so they
                                        keep firing on the server, but the
                                        operator-facing input was deemed too
                                        technical to maintain. */}

                                    {/* Custom Safe Page URL */}
                                    <div>
                                      <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                                        {t('advanced.cloaking.advancedDisclosure.customUrlLabel')}
                                      </label>
                                      <input
                                        type="url"
                                        value={safePageUrl}
                                        onChange={(e) => setSafePageUrl(e.target.value)}
                                        placeholder={t('advanced.cloaking.advancedDisclosure.customUrlPlaceholder')}
                                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      />
                                      <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                                        {t('advanced.cloaking.advancedDisclosure.customUrlHint')}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Device-based Redirect Section — Pro+ in the new
                    pricing model (PLAN_LIMITS.features.redirect).
                    Server-side checkRedirectAccess() in routes/pages.js
                    is the actual gate. */}
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Settings size={20} className="text-green-400" />
                    {t('advanced.deviceRedirect.title')}
                    {hasFeature(effectivePlan, 'redirect') ? (
                      <span className="text-xs px-2 py-1 bg-green-600 rounded-full text-white font-medium">PRO</span>
                    ) : (
                      <span className="text-xs px-2 py-1 bg-blue-600 rounded-full">PRO</span>
                    )}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">
                    {t('advanced.deviceRedirect.description')}
                  </p>
                  
                  {!hasFeature(effectivePlan, 'redirect') ? (
                    <div className="p-4 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-lg border border-green-500/30">
                      <div className="flex items-start gap-3 mb-3">
                        <Settings className="text-green-400 flex-shrink-0 mt-0.5" size={20} />
                        <div>
                          <h4 className="font-semibold text-white mb-1">{t('advanced.deviceRedirect.lockedTitle')}</h4>
                          <p className="text-sm text-gray-300 mb-2">
                            {t('advanced.deviceRedirect.lockedDesc')}
                          </p>
                          <ul className="text-sm text-gray-400 space-y-1 ml-4 list-disc">
                            <li>{t('advanced.deviceRedirect.lockedFeatures.f1')}</li>
                            <li>{t('advanced.deviceRedirect.lockedFeatures.f2')}</li>
                            <li>{t('advanced.deviceRedirect.lockedFeatures.f3')}</li>
                            <li>{t('advanced.deviceRedirect.lockedFeatures.f4')}</li>
                          </ul>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate('/upgrade')}
                        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm"
                      >
                        {t('advanced.deviceRedirect.lockedCta')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <ToggleSwitch
                          label={t('advanced.deviceRedirect.enableLabel')}
                          checked={redirectEnabled}
                          onChange={setRedirectEnabled}
                          description={t('advanced.deviceRedirect.enableDesc')}
                        />
                        <span className="text-xs px-2 py-1 bg-green-600 rounded-full text-white font-medium">PRO</span>
                      </div>
                      
                      {redirectEnabled && (
                        <div className="ml-4 pl-4 border-l-2 border-green-500 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                              {t('advanced.deviceRedirect.desktop.label')}
                            </label>
                            <input
                              type="url"
                              value={desktopUrl}
                              onChange={(e) => setDesktopUrl(e.target.value)}
                              placeholder={t('advanced.deviceRedirect.desktop.placeholder')}
                              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">{t('advanced.deviceRedirect.desktop.hint')}</p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                              {t('advanced.deviceRedirect.mobile.label')}
                            </label>
                            <input
                              type="url"
                              value={mobileUrl}
                              onChange={(e) => setMobileUrl(e.target.value)}
                              placeholder={t('advanced.deviceRedirect.mobile.placeholder')}
                              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">{t('advanced.deviceRedirect.mobile.hint')}</p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                              {t('advanced.deviceRedirect.tablet.label')} <span className="text-xs text-gray-500">{t('advanced.deviceRedirect.tablet.labelHint')}</span>
                            </label>
                            <input
                              type="url"
                              value={tabletUrl}
                              onChange={(e) => setTabletUrl(e.target.value)}
                              placeholder={t('advanced.deviceRedirect.tablet.placeholder')}
                              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">{t('advanced.deviceRedirect.tablet.hint')}</p>
                          </div>

                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-4">
                            <p className="text-sm text-blue-300 font-medium mb-2">{t('advanced.deviceRedirect.howItWorks')}</p>
                            <ul className="text-xs text-gray-400 space-y-1 ml-4 list-disc">
                              <li>{t('advanced.deviceRedirect.rules.r1')}</li>
                              <li>{t('advanced.deviceRedirect.rules.r2')}</li>
                              <li>{t('advanced.deviceRedirect.rules.r3')}</li>
                              <li>{t('advanced.deviceRedirect.rules.r4')}</li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 5 && (
          <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            <div className="max-w-2xl w-full mx-auto my-8 p-6">
              {/* Animated Background Glow */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
              </div>

              {/* Main Card */}
              <div className="relative bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-700/50 overflow-hidden">
                {/* Top Gradient Border */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 animate-gradient"></div>
                
                <div className="p-8 md:p-12 text-center">
                  {/* Icon with Animation */}
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-xl animate-pulse"></div>
                    <div className="relative bg-gradient-to-br from-blue-500 to-purple-600 p-4 rounded-2xl shadow-lg transform hover:scale-110 transition-transform duration-300">
                      <Upload size={48} className="text-white" />
                    </div>
                  </div>

                  {/* Title with Gradient */}
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent animate-gradient">
                    {t('publishStep.title')}
                  </h2>
                  
                  {/* Description */}
                  <p className="text-gray-400 text-lg mb-8 max-w-md mx-auto leading-relaxed">
                    {t('publishStep.description')}
                  </p>

                  {/* Action Buttons */}
                  <div className="space-y-4">
                    {/* Publish Button */}
                    <button 
                      onClick={handlePublishToDomain}
                      disabled={isPublishing}
                      className="group relative w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-xl font-semibold text-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-blue-500/50 transform hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
                    >
                      {/* Button Shine Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                      
                      <span className="relative flex items-center justify-center gap-2">
                        {isPublishing ? (
                          <>
                            <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                            {t('publishStep.publishing')}
                          </>
                        ) : (
                          <>
                            🚀 {t('publishStep.publish')}
                          </>
                        )}
                      </span>
                    </button>

                    {/* Back to Dashboard Button */}
                    <button 
                      onClick={() => navigate('/dashboard')}
                      className="group w-full px-8 py-4 bg-gray-800/50 hover:bg-gray-700/50 text-gray-300 hover:text-white rounded-xl font-medium text-lg transition-all duration-300 border border-gray-700/50 hover:border-gray-600 flex items-center justify-center gap-2 transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <LayoutDashboard size={20} className="group-hover:-translate-x-1 transition-transform duration-300" />
                      {t('publishStep.backToDashboard')}
                    </button>
                  </div>

                  {/* Quick Stats or Info */}
                  <div className="mt-8 pt-6 border-t border-gray-700/50">
                    <div className="flex items-center justify-center gap-6 text-sm text-gray-400">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span>{t('publishStep.pageReady')}</span>
                      </div>
                      <div className="w-1 h-4 bg-gray-700 rounded-full"></div>
                      <div className="flex items-center gap-2">
                        <Shield size={14} />
                        <span>{t('publishStep.sslSecured')}</span>
                      </div>
                      <div className="w-1 h-4 bg-gray-700 rounded-full"></div>
                      <div className="flex items-center gap-2">
                        <Crown size={14} className="text-yellow-500" />
                        <span>{effectivePlan.toUpperCase() || t('publishStep.planFallback')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tips Card (Optional) */}
              <div className="mt-6 bg-gray-800/30 backdrop-blur-sm rounded-xl p-4 border border-gray-700/30">
                <p className="text-sm text-gray-400 text-center">
                  💡 <span className="text-gray-300 font-medium">{t('publishStep.proTipLabel')}</span> {t('publishStep.proTipBody')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Slug Modal */}
      {currentPage && showSlugModal && (
        <EditSlugModal
          page={{
            id: currentPage.id,
            slug: (currentPage as any).slug || currentPage.id,
            name: currentPage.name
          }}
          isOpen={showSlugModal}
          onClose={() => setShowSlugModal(false)}
          onSuccess={(newSlug) => {
            setShowSlugModal(false);
            // Update current page with new slug
            setCurrentPage({
              ...currentPage,
              ...{ slug: newSlug }
            } as any);
            toast.success(t('toast.slugUpdated'));
          }}
        />
      )}
    </div>
  );
};

